import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Chip, Popover, PopoverContent, PopoverTrigger } from "@heroui/react";
import { LinkIcon } from "@heroicons/react/24/outline";
import clsx from "clsx";

// ============================================================
// Helper functions
// ============================================================

function uniqueStrings(values = []) {
  return [...new Set(Array.isArray(values) ? values : [values].filter(Boolean).map(String))];
}

function toArray(value) {
  if (value === undefined || value === null) return [];
  return Array.isArray(value) ? value : [value];
}

function truncateText(value = "", maxLength = 120) {
  const text = String(value ?? "").trim();
  if (!text || text.length <= maxLength) return text;
  return `${text.slice(0, maxLength - 1).trim()}…`;
}

function normalizeAgentLifecycleStatus(value = "") {
  const status = String(value ?? "").trim().toLowerCase();
  if (!status) return "blocked";
  if (["awaiting_review", "awaiting_children", "running", "ready", "ready_to_reason", "blocked", "completed", "answered", "deferred"].includes(status)) return status;
  if (["active", "in_progress", "partial"].includes(status)) return "running";
  if (["queued", "planned", "open", "funded"].includes(status)) return "ready";
  if (["closed", "answered", "satisfied", "done", "resolved", "verified"].includes(status)) return "completed";
  if (["failed", "error", "exhausted", "canceled", "cancelled"].includes(status)) return "blocked";
  return "blocked";
}

function isTerminalAgentLifecycleStatus(value = "") {
  return ["completed", "answered", "blocked", "deferred"].includes(normalizeAgentLifecycleStatus(value));
}

function humanizeKey(value = "", fallback = "Unassigned") {
  const text = String(value ?? "").trim();
  if (!text) return fallback;
  return text.replace(/[_-]+/g, " ");
}

function titleizeKey(value = "", fallback = "Unassigned") {
  return humanizeKey(value, fallback).replace(/\b\w/g, (char) => char.toUpperCase());
}

function formatAgentLifecycleLabel(value = "") {
  const status = normalizeAgentLifecycleStatus(value);
  if (status === "awaiting_review") return "Awaiting Review";
  if (status === "awaiting_children") return "Awaiting Children";
  if (status === "running") return "Active";
  if (status === "ready") return "Ready";
  if (status === "ready_to_reason") return "Ready To Reason";
  if (status === "completed") return "Completed";
  if (status === "answered") return "Answered";
  if (status === "deferred") return "Deferred";
  return "Blocked";
}

function getNormalizedTaskStatus(task) {
  return normalizeAgentLifecycleStatus(task?.kanbanStatus ?? task?.status);
}

function getRawTaskStatus(task) {
  return String(task?.kanbanStatus ?? task?.status ?? "").trim().toLowerCase();
}

function isActiveTaskStatus(status) {
  return normalizeAgentLifecycleStatus(status) === "running";
}

function getTaskLifecyclePriority(task) {
  const rawStatus = getRawTaskStatus(task);
  const status = getNormalizedTaskStatus(task);
  if (isActiveTaskStatus(status)) return 0;
  if (["queued", "ready", "ready_to_reason", "planned"].includes(rawStatus) || ["ready", "ready_to_reason"].includes(status)) return 1;
  if (["failed", "error", "exhausted", "canceled", "cancelled"].includes(rawStatus)) return 2;
  if (status === "completed") return 3;
  return 4;
}

function compareTasksForDisplay(left, right) {
  const priorityDelta = getTaskLifecyclePriority(left) - getTaskLifecyclePriority(right);
  if (priorityDelta !== 0) return priorityDelta;
  const leftQueuedAt = Number(left?.queued_at_ms ?? 0);
  const rightQueuedAt = Number(right?.queued_at_ms ?? 0);
  if (leftQueuedAt !== rightQueuedAt) return rightQueuedAt - leftQueuedAt;
  return String(left?.id ?? "").localeCompare(String(right?.id ?? ""));
}

function getTaskDisplayTitle(task) {
  const metadata = task?.metadata ?? {};
  const searchFocus = String(metadata?.search_focus ?? task?.search_focus ?? "").trim().toLowerCase();
  if (searchFocus === "matrix_cell_fill") {
    const peer = String(metadata?.peer_label ?? metadata?.peer_name ?? metadata?.entity_name ?? metadata?.target_name ?? "").trim();
    const theme = String(metadata?.theme_label ?? metadata?.topic_label ?? metadata?.theme_id ?? "").trim();
    if (peer && theme) return `Research ${titleizeKey(theme)} for ${peer}`;
    if (peer) return `Research matrix cell for ${peer}`;
  }
  if (searchFocus === "peer_set_expand") {
    const target = String(metadata?.target_name ?? metadata?.entity_name ?? "").trim();
    const taskType = String(task?.type ?? "").trim();
    const query = String(task?.query ?? "").trim();
    const title = String(metadata?.title ?? "").trim();
    const url = String(metadata?.url ?? query).trim();
    if (target && taskType === "resolve_company_domain") {
      return `Resolve company domain for ${target}`;
    }
    if (taskType === "grounded_query") {
      return "Identify peer set";
    }
    if (taskType === "search_google") {
      return query ? `Search for peer set: ${query}` : "Search for peer set";
    }
    if (taskType === "fetch_page") {
      if (title) return `Inspect peer source: ${title}`;
      if (url) {
        try {
          return `Inspect peer source: ${new URL(url).hostname.replace(/^www\./, "")}`;
        } catch {
          return `Inspect peer source: ${url}`;
        }
      }
      return "Inspect peer source";
    }
    return "Identify peer set";
  }
  return task?.query || metadata?.title || task?.id || "Untitled task";
}

function getAgentStatusColor(status) {
  const normalized = normalizeAgentLifecycleStatus(status);
  switch (normalized) {
    case "running": return { bg: "bg-blue-500", light: "bg-blue-50", text: "text-blue-700", border: "border-blue-200", ring: "ring-blue-500" };
    case "awaiting_review": return { bg: "bg-amber-500", light: "bg-amber-50", text: "text-amber-700", border: "border-amber-200", ring: "ring-amber-500" };
    case "ready":
    case "ready_to_reason": return { bg: "bg-slate-400", light: "bg-slate-50", text: "text-slate-600", border: "border-slate-200", ring: "ring-slate-400" };
    case "completed":
    case "answered": return { bg: "bg-emerald-500", light: "bg-emerald-50", text: "text-emerald-700", border: "border-emerald-200", ring: "ring-emerald-500" };
    case "blocked": return { bg: "bg-rose-500", light: "bg-rose-50", text: "text-rose-700", border: "border-rose-200", ring: "ring-rose-500" };
    default: return { bg: "bg-slate-400", light: "bg-slate-50", text: "text-slate-600", border: "border-slate-200", ring: "ring-slate-400" };
  }
}

function getRoleLabel(agent) {
  if (agent?.node_role === "root") return "RESEARCH";
  if (agent?.node_role === "objective") return "OBJECTIVE";
  if (agent?.node_role === "question") return "QUESTION";
  if (agent?.kind === "section_writer") return "WRITER";
  if (agent?.kind === "frame_setting") return "FRAME";
  if (agent?.kind === "selection_setting") return "SELECTION";
  if (agent?.kind === "synthesis_producing") return "SYNTHESIS";
  if (agent?.kind === "evidence_producing") return "EVIDENCE";
  if (agent?.scope_type === "section") return "SECTION";
  if (agent?.scope_type === "focus_area") return "FOCUS";
  return "AGENT";
}

function getLifecycleTimestampMs(entry = {}) {
  const numericCandidates = [
    entry?.started_at_ms,
    entry?.completed_at_ms,
    entry?.updated_at_ms,
  ]
    .map((value) => Number(value))
    .filter((value) => Number.isFinite(value) && value > 0);
  if (numericCandidates.length) {
    return Math.max(...numericCandidates);
  }
  const dateCandidates = [
    entry?.completed_at,
    entry?.started_at,
    entry?.updated_at,
    entry?.updatedAt,
    entry?.created_at,
    entry?.createdAt,
  ]
    .map((value) => (value ? new Date(value).getTime() : NaN))
    .filter((value) => Number.isFinite(value) && value > 0);
  return dateCandidates.length ? Math.max(...dateCandidates) : null;
}

function formatRelativeAge(timestampMs = null) {
  if (!Number.isFinite(Number(timestampMs)) || Number(timestampMs) <= 0) {
    return null;
  }
  const deltaMs = Math.max(0, Date.now() - Number(timestampMs));
  if (deltaMs < 15000) return "just now";
  if (deltaMs < 60000) return `${Math.round(deltaMs / 1000)}s ago`;
  if (deltaMs < 3600000) return `${Math.round(deltaMs / 60000)}m ago`;
  if (deltaMs < 86400000) return `${Math.round(deltaMs / 3600000)}h ago`;
  return `${Math.round(deltaMs / 86400000)}d ago`;
}

function formatDurationCompact(value) {
  const durationMs = Number(value ?? 0);
  if (!Number.isFinite(durationMs) || durationMs <= 0) {
    return null;
  }
  const totalSeconds = Math.max(1, Math.round(durationMs / 1000));
  if (totalSeconds < 60) {
    return `${totalSeconds}s`;
  }
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  if (!seconds) {
    return `${minutes}m`;
  }
  return `${minutes}m ${seconds}s`;
}

function getRoleIcon(agent) {
  // Simple colored circle with first letter
  const label = getRoleLabel(agent);
  const colors = {
    RESEARCH: "bg-blue-500",
    OBJECTIVE: "bg-violet-500",
    QUESTION: "bg-purple-500",
    WRITER: "bg-emerald-500",
    FRAME: "bg-orange-500",
    SELECTION: "bg-amber-500",
    SYNTHESIS: "bg-teal-500",
    EVIDENCE: "bg-cyan-500",
    SECTION: "bg-indigo-500",
    FOCUS: "bg-pink-500",
    AGENT: "bg-slate-500",
  };
  return { letter: label[0] ?? "A", color: colors[label] || "bg-slate-500", label };
}

function getPrimaryText(agent) {
  const wp = agent?.work_package ?? {};
  const candidates = [
    wp?.objective,
    wp?.question,
    wp?.mission,
    agent?.label,
    agent?.display_label,
    agent?.scope_key,
    agent?.id,
  ];
  for (const c of candidates) {
    const t = String(c ?? "").trim();
    if (t) return titleizeKey(t, t);
  }
  return "Untitled agent";
}

function getSecondaryText(agent) {
  const wp = agent?.work_package ?? {};
  const primary = getPrimaryText(agent);
  const candidates = [wp?.mission, agent?.status_text];
  for (const c of candidates) {
    const t = String(c ?? "").trim();
    if (t && t !== primary) return t;
  }
  return "";
}

function getElapsedText(agent) {
  // Mock elapsed based on status for now - could be real timestamps
  return "";
}

// ============================================================
// Task progress bar — shows done / total tasks as a real metric
// ============================================================

function useClock(intervalMs = 1000) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), intervalMs);
    return () => clearInterval(id);
  }, [intervalMs]);
  return now;
}

function formatClockTime(ms) {
  if (!Number.isFinite(Number(ms))) return "--:--";
  const d = new Date(Number(ms));
  const h = d.getHours().toString().padStart(2, "0");
  const m = d.getMinutes().toString().padStart(2, "0");
  const s = d.getSeconds().toString().padStart(2, "0");
  return `${h}:${m}:${s}`;
}

function formatRuntimeFromStart(startMs, nowMs) {
  if (!Number.isFinite(Number(startMs)) || Number(startMs) <= 0) return "--:--:--";
  const totalSec = Math.max(0, Math.floor((nowMs - startMs) / 1000));
  const h = Math.floor(totalSec / 3600).toString().padStart(2, "0");
  const m = Math.floor((totalSec % 3600) / 60).toString().padStart(2, "0");
  const s = (totalSec % 60).toString().padStart(2, "0");
  return `${h}:${m}:${s}`;
}

function getAgentStartedAtMs(entry = {}) {
  const candidates = [
    Number(entry?.started_at_ms),
    entry?.started_at ? new Date(entry.started_at).getTime() : NaN,
    Number(entry?.created_at_ms),
    entry?.created_at ? new Date(entry.created_at).getTime() : NaN,
  ].filter((v) => Number.isFinite(v) && v > 0);
  return candidates.length ? Math.min(...candidates) : null;
}

function LiveIndicator({ active = true }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className={clsx("relative inline-flex h-2 w-2", active && "")}>
        <span className={clsx("absolute inline-flex h-full w-full rounded-full opacity-75", active ? "bg-emerald-500 animate-ping" : "bg-slate-400")} />
        <span className={clsx("relative inline-flex h-2 w-2 rounded-full", active ? "bg-emerald-500" : "bg-slate-400")} />
      </span>
      <span className={clsx("text-[10px] font-bold uppercase tracking-wider", active ? "text-emerald-600" : "text-slate-500")}>
        {active ? "Live" : "Idle"}
      </span>
    </span>
  );
}

function TaskProgressBar({ done, total, color = "blue" }) {
  if (total === 0) return null;
  const pct = Math.min(100, Math.max(0, Math.round((done / total) * 100)));
  const colorMap = {
    blue: "bg-blue-500",
    emerald: "bg-emerald-500",
    amber: "bg-amber-500",
    rose: "bg-rose-500",
    slate: "bg-slate-400",
  };
  const barColor = colorMap[color] || colorMap.slate;

  return (
    <div className="w-16">
      <div className="h-1.5 w-full overflow-hidden rounded-full bg-slate-100">
        <div className={clsx("h-full rounded-full transition-all duration-500", barColor)} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

// ============================================================
// Status icon for agent row
// ============================================================

function StatusDot({ status, forceActive = false }) {
  const normalized = normalizeAgentLifecycleStatus(status);
  const color = forceActive
    ? { bg: "bg-blue-500" }
    : getAgentStatusColor(status);
  const pulsing = forceActive || normalized === "running";
  return (
    <span className={clsx("relative inline-flex h-2.5 w-2.5", pulsing && "animate-pulse")}>
      <span className={clsx("absolute inline-flex h-full w-full rounded-full opacity-75", color.bg, pulsing && "animate-ping")} />
      <span className={clsx("relative inline-flex h-2.5 w-2.5 rounded-full", color.bg)} />
    </span>
  );
}

// ============================================================
// Agent overview content — shows actual result findings first
// ============================================================

function normalizeAgentDisplayText(value) {
  return String(value ?? "")
    .toLowerCase()
    // Strip common framing prefixes so "Verify claim: X", "Objective: X",
    // and "Question: X" all collapse to the same content for dedup checks.
    .replace(/^\s*(verify\s+(?:the\s+)?(?:bound\s+)?claim|verify|objective|question|mission|goal|task|why\s*now)\s*[:\-—]\s*/i, "")
    .replace(/^["“”']+|["“”']+$/g, "")
    .replace(/\s+/g, " ")
    .trim();
}

// Returns true if `candidate` is effectively the same content as any of
// the `seen` strings — either identical, or one substring-contains the
// other once normalized. Used to suppress duplicated mission/objective/
// question/summary text in the expanded panel.
//
// Substring containment only counts as a duplicate when the two strings are
// roughly similar in length (shorter ≥ 40 % of longer). This prevents a
// short phrase like "Recent announcements" from being treated as a duplicate
// of a long descriptive sentence like "What are the most relevant recent
// announcements from Diageo?".
function isDuplicateAgentText(candidate, seen, { minLength = 12 } = {}) {
  const c = normalizeAgentDisplayText(candidate);
  if (!c) return true;
  for (const item of seen) {
    const s = normalizeAgentDisplayText(item);
    if (!s) continue;
    if (c === s) return true;
    if (c.length >= minLength && s.length >= minLength) {
      const longer = Math.max(c.length, s.length);
      const shorter = Math.min(c.length, s.length);
      // Require the shorter string to be a significant fraction of the
      // longer one (≥ 40 %) before treating containment as a duplicate.
      if (shorter / longer >= 0.4) {
        if (c.includes(s) || s.includes(c)) return true;
      }
    }
  }
  return false;
}

// ============================================================
// Responsive hook — detects if the viewport is at least md (768px)
// ============================================================

function useIsWideScreen(breakpoint = 768) {
  const [isWide, setIsWide] = useState(() =>
    typeof window !== "undefined" ? window.innerWidth >= breakpoint : false,
  );
  useEffect(() => {
    const mq = window.matchMedia(`(min-width: ${breakpoint}px)`);
    const handler = () => setIsWide(window.innerWidth >= breakpoint);
    setIsWide(mq.matches);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, [breakpoint]);
  return isWide;
}

// ============================================================
// Paginated list — reusable pagination with page-size selector
// ============================================================

function PaginatedList({ items, pageSizeOptions = [10, 20, 50], children, emptyMessage = "No items." }) {
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(pageSizeOptions[0] ?? 20);
  const totalPages = Math.max(1, Math.ceil(items.length / pageSize));

  // Reset to page 0 when page size changes
  useEffect(() => {
    if (pageIndex >= totalPages) setPageIndex(0);
  }, [totalPages, pageIndex]);

  const pageItems = items.slice(pageIndex * pageSize, (pageIndex + 1) * pageSize);

  if (items.length === 0) return <p className="text-sm text-slate-500">{emptyMessage}</p>;

  return (
    <div>
      <div className="space-y-2">
        {children(pageItems)}
      </div>
      {totalPages > 1 && (
        <div className="mt-3 flex items-center justify-between border-t border-slate-200 pt-3">
          <div className="flex items-center gap-2 text-xs text-slate-500">
            <span>Rows per page:</span>
            <select
              value={pageSize}
              onChange={(e) => {
                setPageSize(Number(e.target.value));
                setPageIndex(0);
              }}
              className="rounded border border-slate-300 bg-white px-2 py-0.5 text-xs font-medium text-slate-700 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            >
              {pageSizeOptions.map((size) => (
                <option key={size} value={size}>{size}</option>
              ))}
            </select>
            <span>
              {pageIndex * pageSize + 1}–{Math.min((pageIndex + 1) * pageSize, items.length)} of {items.length}
            </span>
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setPageIndex(0)}
              disabled={pageIndex === 0}
              className="rounded-md px-2 py-1 text-xs font-medium text-slate-600 disabled:opacity-40 hover:bg-slate-100"
            >
              First
            </button>
            <button
              onClick={() => setPageIndex((p) => Math.max(0, p - 1))}
              disabled={pageIndex === 0}
              className="rounded-md px-2 py-1 text-xs font-medium text-slate-600 disabled:opacity-40 hover:bg-slate-100"
            >
              ← Prev
            </button>
            <span className="px-2 text-xs text-slate-500">
              Page {pageIndex + 1} of {totalPages}
            </span>
            <button
              onClick={() => setPageIndex((p) => Math.min(totalPages - 1, p + 1))}
              disabled={pageIndex >= totalPages - 1}
              className="rounded-md px-2 py-1 text-xs font-medium text-slate-600 disabled:opacity-40 hover:bg-slate-100"
            >
              Next →
            </button>
            <button
              onClick={() => setPageIndex(totalPages - 1)}
              disabled={pageIndex >= totalPages - 1}
              className="rounded-md px-2 py-1 text-xs font-medium text-slate-600 disabled:opacity-40 hover:bg-slate-100"
            >
              Last
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================
// Reusable claim row with hover-to-reveal source URLs
// ============================================================

function ClaimRow({ claim, compact = false, showStatusDot = true }) {
  const sources = uniqueStrings([
    ...(toArray(claim?.source_urls) || []),
    ...(claim?.source_url ? [claim.source_url] : []),
    ...(toArray(claim?.source_ids) || []).filter((id) => typeof id === "string" && /^https?:\/\//.test(id)),
    ...(toArray(claim?.metadata?.resolved_source_urls) || []).filter((id) => typeof id === "string" && /^https?:\/\//.test(id)),
    ...(toArray(claim?.metadata?.grounded_candidate_source_ids) || []).filter((id) => typeof id === "string" && /^https?:\/\//.test(id)),
  ]).filter(Boolean);

  const getDomain = (url) => {
    try { return new URL(url).hostname.replace(/^www\./, ""); } catch { return url; }
  };

  const getSourcePath = (url) => {
    try { return new URL(url).pathname + new URL(url).search; } catch { return ""; }
  };

  return (
    <div className="relative">
      <Popover placement="bottom" offset={4} showArrow>
        <PopoverTrigger>
          <div className={clsx("cursor-pointer", showStatusDot ? "flex gap-1.5" : "")}>
            {showStatusDot && (
              <span
                className={clsx(
                  "mt-0.5 h-1.5 w-1.5 shrink-0 rounded-full",
                  claim?.status === "verified" ? "bg-emerald-500" :
                  claim?.status === "rejected" ? "bg-rose-500" :
                  claim?.status === "pending" ? "bg-amber-500" : "bg-slate-400"
                )}
              />
            )}
            <span className={clsx(
              "text-slate-700 leading-snug",
              compact ? "text-[11px]" : "text-[11px] text-slate-800"
            )}>
              {truncateText(claim.text || claim.id, compact ? 110 : 200)}
            </span>
          </div>
        </PopoverTrigger>
        <PopoverContent className="w-[420px] overflow-hidden p-3 shadow-lg">
          <div className="w-full min-w-[280px]">
            <p className="flex items-center gap-1.5 text-[9px] font-semibold uppercase tracking-wider text-slate-400 mb-2">
              <LinkIcon className="h-3 w-3 shrink-0 text-slate-400" />
              Sources
            </p>
            <div className="space-y-2">
              {sources.map((src, i) => (
                <a
                  key={i}
                  href={src}
                  target="_blank"
                  rel="noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  className="flex w-full gap-2.5 rounded-lg border border-slate-100 bg-white p-2 transition-colors hover:bg-slate-50"
                >
                  <img
                    src={`/api/preview?url=${encodeURIComponent(src)}`}
                    alt=""
                    className="h-12 w-12 shrink-0 rounded-md object-cover bg-slate-100"
                    onError={(e) => { e.target.style.display = "none"; }}
                  />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-[11px] font-medium text-slate-800">
                      {getDomain(src)}
                    </p>
                    <p className="truncate text-[9px] text-slate-400">
                      {getSourcePath(src)}
                    </p>
                  </div>
                </a>
              ))}
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}

function AgentOverviewContent({ agent, tasks, activeTasks, queuedTasks, doneTasks, claims, onSwitchTab }) {
  const wp = agent?.work_package ?? {};
  const result = agent?.result ?? {};

  const summaryShort = String(result?.summary_short ?? "").trim();
  const summaryLong = String(result?.summary_long ?? "").trim();
  const synthesizedByLlm = Boolean(result?.synthesized_by_llm);

  // Mission Statement — show distinct mission, objective, question
  const missionRaw = String(wp?.mission ?? "").trim();
  const objectiveRaw = String(wp?.objective ?? "").trim();
  const questionRaw = String(wp?.question ?? "").trim();
  // Title text that already shows in the row header (avoid repeating it).
  const headerTitle = getPrimaryText(agent) ?? "";
  const missionFields = [
    { label: "Mission", value: missionRaw },
    { label: "Objective", value: objectiveRaw },
    { label: "Question", value: questionRaw },
  ].filter((f) => f.value);
  // Dedupe with substring containment + prefix-stripping so e.g.
  // "Verify claim: X" and "Objective: Verify claim X" collapse to one row.
  // Substring containment only counts when the shorter string is ≥ 40 % of
  // the longer one — prevents a short phrase like "Recent announcements"
  // from being treated as a duplicate of a long question.
  const seenMissionValues = [headerTitle];
  const visibleMissionFields = [];
  for (const f of missionFields) {
    if (isDuplicateAgentText(f.value, seenMissionValues)) continue;
    seenMissionValues.push(f.value);
    visibleMissionFields.push(f);
  }

  // Started + runtime. For settled agents (completed/blocked/answered/etc.),
  // freeze the runtime at completed_at instead of letting it tick up.
  const startedAtMs = getAgentStartedAtMs(agent);
  const completedAtMs = (() => {
    const candidates = [
      Number(agent?.completed_at_ms),
      agent?.completed_at ? new Date(agent.completed_at).getTime() : NaN,
    ].filter((v) => Number.isFinite(v) && v > 0);
    return candidates.length ? Math.max(...candidates) : null;
  })();
  const overviewLifecycleStatus = normalizeAgentLifecycleStatus(agent?.status);
  const isSettled = ["completed", "answered", "blocked", "deferred", "failed", "canceled"].includes(overviewLifecycleStatus);
  const nowForRuntime = useClock(1000);
  const runtimeEndMs = (isSettled && completedAtMs) ? completedAtMs : nowForRuntime;
  const startedAtLabel = startedAtMs ? formatClockTime(startedAtMs) : "—";
  const runtimeLabel = startedAtMs
    ? formatRuntimeFromStart(startedAtMs, runtimeEndMs)
    : (Number.isFinite(Number(agent?.duration_ms)) && Number(agent.duration_ms) > 0
      ? formatDurationCompact(agent.duration_ms) ?? "—"
      : "—");

  // Linked objectives (use objective_ids, slice to a few)
  const linkedObjectives = uniqueStrings(toArray(agent?.objective_ids)).slice(0, 4);

  // Priority indicator from work_package or scheduling fields
  const priorityRaw = String(wp?.priority ?? agent?.priority ?? "").trim().toLowerCase();
  const priorityLabel = priorityRaw
    ? priorityRaw === "high" || priorityRaw === "urgent"
      ? { text: "High", color: "text-rose-600", icon: "↑" }
      : priorityRaw === "low"
      ? { text: "Low", color: "text-slate-500", icon: "↓" }
      : { text: titleizeKey(priorityRaw), color: "text-slate-600", icon: "•" }
    : null;

  // Tasks display: aggregate active + queued, with progress bars
  const tasksForDisplay = [...activeTasks, ...queuedTasks].slice(0, 5);
  const overflowTaskCount = Math.max(0, activeTasks.length + queuedTasks.length - tasksForDisplay.length);

  const lifecycleStatus = normalizeAgentLifecycleStatus(agent?.status);
  const statusColor = getAgentStatusColor(agent?.status);

  // For completed/blocked agents with no runnable tasks, hide the Active Tasks
  // column and let Claims Produced span 2 columns. If stale agent status still
  // has live tasks beneath it, surface those tasks.
  const hideActiveTasksColumn = ["completed", "answered", "blocked"].includes(lifecycleStatus) && tasksForDisplay.length === 0;
  const claimsClaimSpan = hideActiveTasksColumn ? "md:col-span-2" : "";
  const claimsListCols = hideActiveTasksColumn ? "md:grid-cols-2" : "md:grid-cols-1";

  return (
    <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
      {/* === Column 1: Mission Statement === */}
      <div className="space-y-3">
        <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-400">Mission Statement</p>
        {visibleMissionFields.length === 0 ? (
          <p className="text-xs italic text-slate-400">No mission defined</p>
        ) : (
          <div className="space-y-1.5">
            {visibleMissionFields.map((f, idx) => (
              <p key={f.label} className={clsx(
                "text-xs leading-relaxed",
                idx === 0 ? "font-medium text-slate-800" : "text-slate-600",
              )}>
                {idx > 0 && <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mr-1">{f.label}:</span>}
                {f.value}
              </p>
            ))}
          </div>
        )}

        <dl className="space-y-1.5 text-[11px]">
          <div className="flex items-center gap-2">
            <dt className="w-16 shrink-0 text-slate-400">Status</dt>
            <dd>
              <span className={clsx("rounded px-1.5 py-0.5 text-[10px] font-medium", statusColor.light, statusColor.text)}>
                {formatAgentLifecycleLabel(agent?.status)}
              </span>
            </dd>
          </div>
          <div className="flex items-center gap-2">
            <dt className="w-16 shrink-0 text-slate-400">Started</dt>
            <dd className="font-mono text-slate-700">{startedAtLabel}</dd>
          </div>
          <div className="flex items-center gap-2">
            <dt className="w-16 shrink-0 text-slate-400">Runtime</dt>
            <dd className="font-mono text-slate-700">{runtimeLabel}</dd>
          </div>
          {priorityLabel && (
            <div className="flex items-center gap-2">
              <dt className="w-16 shrink-0 text-slate-400">Priority</dt>
              <dd className={clsx("flex items-center gap-1 font-medium", priorityLabel.color)}>
                <span>{priorityLabel.icon}</span>
                {priorityLabel.text}
              </dd>
            </div>
          )}
          {linkedObjectives.length > 0 && (
            <div className="flex items-start gap-2">
              <dt className="w-16 shrink-0 text-slate-400">Linked</dt>
              <dd className="flex flex-wrap gap-1">
                {linkedObjectives.map((id) => (
                  <span
                    key={id}
                    title={id}
                    className="rounded bg-slate-100 px-1.5 py-0.5 font-mono text-[9px] text-slate-600"
                  >
                    {truncateText(id.replace(/^obj[_:-]?/i, "OBJ-"), 12)}
                  </span>
                ))}
              </dd>
            </div>
          )}
        </dl>
      </div>

      {/* === Column 2: Active Tasks (hidden for completed/blocked) === */}
      {!hideActiveTasksColumn && (
      <div className="space-y-3">
        <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-400">
          Active Tasks {(activeTasks.length + queuedTasks.length) > 0 && (
            <span className="ml-1 font-normal text-slate-400">({activeTasks.length + queuedTasks.length})</span>
          )}
        </p>
        {tasksForDisplay.length === 0 ? (
          <p className="text-xs italic text-slate-400">No active or queued tasks</p>
        ) : (
          <div className="space-y-1.5">
            {tasksForDisplay.map((task, i) => {
              const taskStatus = getNormalizedTaskStatus(task);
              const isComplete = taskStatus === "completed";
              const isActive = isActiveTaskStatus(taskStatus);
              const taskTitle = getTaskDisplayTitle(task);
              const taskDoneTotal = task?.progress;
              return (
                <div key={task.id || i} className="flex items-center gap-2">
                  {isComplete ? (
                    <svg className="h-3.5 w-3.5 shrink-0 text-emerald-500" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                  ) : isActive ? (
                    <svg className="h-3.5 w-3.5 shrink-0 animate-spin text-blue-500" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="10" cy="10" r="7" strokeOpacity="0.25" />
                      <path d="M17 10a7 7 0 00-7-7" strokeLinecap="round" />
                    </svg>
                  ) : (
                    <svg className="h-3.5 w-3.5 shrink-0 text-slate-300" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5">
                      <circle cx="10" cy="10" r="7" />
                    </svg>
                  )}
                  <span className={clsx("flex-1 truncate text-[11px]", isComplete ? "text-slate-400 line-through" : "text-slate-700")}>
                    {truncateText(taskTitle, 48)}
                  </span>
                  {taskDoneTotal && Number.isFinite(Number(taskDoneTotal?.total)) && Number(taskDoneTotal.total) > 0 ? (
                    <div className="flex items-center gap-1.5 text-[9px] text-slate-500">
                      <div className="h-1 w-12 overflow-hidden rounded-full bg-slate-100">
                        <div
                          className={clsx("h-full", isComplete ? "bg-emerald-500" : "bg-blue-500")}
                          style={{ width: `${Math.min(100, Math.round((Number(taskDoneTotal.done) / Number(taskDoneTotal.total)) * 100))}%` }}
                        />
                      </div>
                      <span className="font-mono">{taskDoneTotal.done ?? 0}/{taskDoneTotal.total}</span>
                    </div>
                  ) : null}
                </div>
              );
            })}
            {overflowTaskCount > 0 && (
              <button
                type="button"
                onClick={() => onSwitchTab?.("tasks")}
                className="text-[11px] text-blue-600 hover:underline"
              >
                + {overflowTaskCount} more tasks
              </button>
            )}
          </div>
        )}
      </div>
      )}

      {/* === Column 3: Claims Produced (spans 2 cols when tasks hidden) === */}
      <div className={clsx("space-y-3", claimsClaimSpan)}>
        <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-400">
          Claims Produced {(claims || []).length > 0 && <span className="ml-1 font-normal text-slate-400">({(claims || []).length})</span>}
        </p>
        {(claims || []).length === 0 ? (
          <p className="text-xs italic text-slate-400">No claims produced yet</p>
        ) : (
          <ul className={clsx("grid gap-x-4 gap-y-1.5 text-[11px] grid-cols-1", claimsListCols)}>
            {(claims || []).slice(0, hideActiveTasksColumn ? 10 : 5).map((claim, i) => (
              <li key={claim.id || i}>
                <ClaimRow claim={claim} compact />
              </li>
            ))}
          </ul>
        )}
        {(claims || []).length > (hideActiveTasksColumn ? 10 : 5) && (
          <button
            type="button"
            onClick={() => onSwitchTab?.("claims")}
            className="inline-flex items-center gap-1 text-[11px] font-medium text-blue-600 hover:underline"
          >
            View all claims
            <svg className="h-3 w-3" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L12.586 11H5a1 1 0 110-2h7.586l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
          </button>
        )}
      </div>
    </div>
  );
}

// ============================================================
// Subtree aggregation — every objective absorbs its principal
// workers (question agent + their descendants). Their claims,
// tasks, and result summaries are surfaced as the objective's
// own primary data rather than hidden inside sub-agents.
// ============================================================

function collectSubtreeAgentIds(rootId, childrenByParentId) {
  const ids = [];
  const seen = new Set();
  const stack = [...(childrenByParentId?.get(rootId) ?? [])];
  while (stack.length) {
    const node = stack.shift();
    const id = String(node?.id ?? "").trim();
    if (!id || seen.has(id)) continue;
    seen.add(id);
    ids.push(id);
    (childrenByParentId?.get(id) ?? []).forEach((child) => stack.push(child));
  }
  return ids;
}

function aggregateSubtreeClaims(agentId, childrenByParentId, claimsByAgentId) {
  const merged = [];
  const seen = new Set();
  const append = (list) => {
    (list ?? []).forEach((claim) => {
      const cid = String(claim?.id ?? "").trim() || `${merged.length}-anon`;
      if (seen.has(cid)) return;
      seen.add(cid);
      merged.push(claim);
    });
  };
  append(claimsByAgentId?.get(agentId));
  collectSubtreeAgentIds(agentId, childrenByParentId).forEach((descendantId) => {
    append(claimsByAgentId?.get(descendantId));
  });
  return merged;
}

// Visible sub-agents = direct children minus any that have been absorbed
// into their parent's principal data. Question agents are absorbed (their
// claims/tasks/result are merged upward), but their own children (task
// owners, evidence agents, sub-objectives) are still surfaced one level up.
function getVisibleSubAgents(parentId, childrenByParentId) {
  const out = [];
  const seen = new Set();
  (childrenByParentId?.get(parentId) ?? []).forEach((child) => {
    if (!child?.id) return;
    if (child?.node_role === "question") {
      (childrenByParentId?.get(child.id) ?? []).forEach((grand) => {
        if (grand?.id && !seen.has(grand.id)) {
          seen.add(grand.id);
          out.push(grand);
        }
      });
      return;
    }
    if (!seen.has(child.id)) {
      seen.add(child.id);
      out.push(child);
    }
  });
  return out;
}

function aggregateSubtreeTasks(agentId, childrenByParentId, tasksByAgentId) {
  const merged = [];
  const seen = new Set();
  const append = (list) => {
    (list ?? []).forEach((task) => {
      const tid = String(task?.id ?? "").trim() || `${merged.length}-anon`;
      if (seen.has(tid)) return;
      seen.add(tid);
      merged.push(task);
    });
  };
  append(tasksByAgentId?.get(agentId));
  collectSubtreeAgentIds(agentId, childrenByParentId).forEach((descendantId) => {
    append(tasksByAgentId?.get(descendantId));
  });
  return merged;
}

// ============================================================
// Active-agents-over-time series for sparklines
// ============================================================

// Walks the subtree under agentId, collects [started_at_ms, completed_at_ms]
// intervals from each agent, samples bucketCount evenly-spaced time points,
// and at each point counts agents whose interval covers that timestamp.
// Build [start, end] intervals for an entity (agent or task) using whichever
// timestamp fields are populated. Returns null when no usable start exists.
function intervalFromEntity(entity, nowMs) {
  if (!entity) return null;
  // Tasks use queued_at / queued_at_ms before they ever transition to
  // running. Agents/objectives/questions use created_at / updated_at.
  // Take the earliest available.
  const toMs = (value) => {
    if (value == null) return NaN;
    if (typeof value === "number") return value;
    const parsed = new Date(value).getTime();
    return Number.isFinite(parsed) ? parsed : NaN;
  };
  const startCandidates = [
    entity?.started_at_ms,
    toMs(entity?.started_at),
    entity?.queued_at_ms,
    toMs(entity?.queued_at),
    entity?.created_at_ms,
    toMs(entity?.created_at),
  ]
    .map((v) => Number(v))
    .filter((v) => Number.isFinite(v) && v > 0);
  if (!startCandidates.length) return null;
  const startMs = Math.min(...startCandidates);
  const endCandidates = [
    entity?.completed_at_ms,
    toMs(entity?.completed_at),
    entity?.updated_at_ms,
    toMs(entity?.updated_at),
  ]
    .map((v) => Number(v))
    .filter((v) => Number.isFinite(v) && v > 0);
  const status = String(entity?.status ?? entity?.kanbanStatus ?? "").trim().toLowerCase();
  const stillOpen = ["running", "active", "in_progress", "queued", "ready", "open", "planned"].includes(status)
    || endCandidates.length === 0;
  const endMs = stillOpen ? nowMs : Math.max(...endCandidates);
  return { start: startMs, end: Math.max(startMs, endMs) };
}

// Bucket intervals into a fixed number of evenly-spaced sample points across
// [tMin, tMax]. At each sample, returns how many intervals cover that time.
function bucketIntervalsToSeries(intervals, tMin, tMax, steps) {
  const series = new Array(steps);
  if (tMax <= tMin) {
    for (let i = 0; i < steps; i++) {
      series[i] = intervals.length;
    }
    return series;
  }
  const span = tMax - tMin;
  for (let i = 0; i < steps; i++) {
    const t = tMin + (span * i) / (steps - 1);
    let count = 0;
    for (let j = 0; j < intervals.length; j++) {
      const iv = intervals[j];
      if (iv.start <= t && iv.end >= t) count += 1;
    }
    series[i] = count;
  }
  return series;
}

// Per-objective subtree series. Walks descendants for both agent and task
// activity. Returns:
//   { tMin, tMax,
//     agents: number[], tasks: number[],
//     series: number[],   // alias for agents (back-compat)
//     peak: number, taskPeak: number,
//     agentCount, taskCount }
// Or null when there's no timing data on either dimension.
function buildActiveAgentsTimeSeries(agentId, childrenByParentId, agentsById, tasksByAgentId, bucketCount = 32) {
  if (!agentId) return null;
  const subtreeAgents = [];
  const subtreeAgentIds = [];
  const seen = new Set();
  const stack = [agentId];
  while (stack.length) {
    const id = stack.shift();
    if (!id || seen.has(id)) continue;
    seen.add(id);
    const node = agentsById?.get(id);
    if (node) subtreeAgents.push(node);
    subtreeAgentIds.push(id);
    (childrenByParentId?.get(id) ?? []).forEach((c) => {
      if (c?.id) stack.push(c.id);
    });
  }
  const nowMs = Date.now();
  const agentIntervals = subtreeAgents
    .map((a) => intervalFromEntity(a, nowMs))
    .filter(Boolean);
  const taskIntervals = [];
  if (tasksByAgentId) {
    const seenTaskIds = new Set();
    subtreeAgentIds.forEach((id) => {
      const tasks = tasksByAgentId.get(id) ?? [];
      tasks.forEach((task) => {
        const taskId = task?.id ?? null;
        if (taskId) {
          if (seenTaskIds.has(taskId)) return;
          seenTaskIds.add(taskId);
        }
        const iv = intervalFromEntity(task, nowMs);
        if (iv) taskIntervals.push(iv);
      });
    });
  }
  if (!agentIntervals.length && !taskIntervals.length) return null;
  const allIntervals = [...agentIntervals, ...taskIntervals];
  const tMin = Math.min(...allIntervals.map((iv) => iv.start));
  const tMax = Math.max(...allIntervals.map((iv) => iv.end));
  const steps = Math.max(2, bucketCount);
  const agents = bucketIntervalsToSeries(agentIntervals, tMin, tMax, steps);
  const tasks = bucketIntervalsToSeries(taskIntervals, tMin, tMax, steps);
  return {
    series: agents, // back-compat
    agents,
    tasks,
    tMin,
    tMax: tMax > tMin ? tMax : tMin + 1,
    peak: agents.length ? Math.max(...agents) : 0,
    taskPeak: tasks.length ? Math.max(...tasks) : 0,
    agentCount: agentIntervals.length,
    taskCount: taskIntervals.length,
  };
}

function ActiveAgentsSparkline({
  data,
  width = 120,
  height = 28,
  stroke = "rgb(59 130 246)",
  fill = "rgba(59,130,246,0.15)",
  taskStroke = "rgb(16 185 129)",
  taskFill = "rgba(16,185,129,0.14)",
}) {
  const agentSeries = data?.agents ?? data?.series ?? [];
  const taskSeries = data?.tasks ?? [];
  if (!agentSeries.length && !taskSeries.length) return null;
  const len = Math.max(agentSeries.length, taskSeries.length);
  if (!len) return null;
  const maxValue = Math.max(
    1,
    data?.peak ?? 0,
    data?.taskPeak ?? 0,
    ...agentSeries,
    ...taskSeries,
  );
  const stepX = len > 1 ? width / (len - 1) : width;
  const yFor = (v) => height - 1 - (v / maxValue) * (height - 2);
  const buildPoints = (series) =>
    series.length
      ? series.map((v, i) => `${(i * stepX).toFixed(1)},${yFor(v).toFixed(1)}`).join(" ")
      : "";
  const agentPoints = buildPoints(agentSeries);
  const taskPoints = buildPoints(taskSeries);
  const buildAreaPoints = (pts, series) =>
    series.length && pts
      ? `0,${height} ${pts} ${((series.length - 1) * stepX).toFixed(1)},${height}`
      : "";
  const agentArea = buildAreaPoints(agentPoints, agentSeries);
  const taskArea = buildAreaPoints(taskPoints, taskSeries);
  const startedAt = data?.tMin ? new Date(data.tMin).toLocaleString() : null;
  const endedAt = data?.tMax ? new Date(data.tMax).toLocaleString() : null;
  const agentPeak = data?.peak ?? (agentSeries.length ? Math.max(...agentSeries) : 0);
  const taskPeak = data?.taskPeak ?? (taskSeries.length ? Math.max(...taskSeries) : 0);
  const title = [
    agentSeries.length && `Peak concurrent agents: ${agentPeak}`,
    taskSeries.length && `Peak concurrent tasks: ${taskPeak}`,
    startedAt && `From: ${startedAt}`,
    endedAt && `To: ${endedAt}`,
  ]
    .filter(Boolean)
    .join("\n");
  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      className="block overflow-visible"
      role="img"
      aria-label={`Active agents (peak ${agentPeak}) and tasks (peak ${taskPeak}) over time`}
    >
      <title>{title}</title>
      {/* Tasks drawn first so the agent line sits on top. */}
      {taskArea && <polygon points={taskArea} fill={taskFill} stroke="none" />}
      {taskPoints && (
        <polyline
          points={taskPoints}
          fill="none"
          stroke={taskStroke}
          strokeWidth="1.25"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeDasharray="2 1.5"
        />
      )}
      {agentArea && <polygon points={agentArea} fill={fill} stroke="none" />}
      {agentPoints && (
        <polyline
          points={agentPoints}
          fill="none"
          stroke={stroke}
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      )}
    </svg>
  );
}

// ============================================================
// Descendant rollup — summarize all agents under a given parent
// ============================================================

function summarizeDescendantWorkUnit(agent, childrenByParentId, tasksByAgentId) {
  const agentId = String(agent?.id ?? "").trim();
  const status = normalizeAgentLifecycleStatus(agent?.status);
  const subtreeTasks = aggregateSubtreeTasks(agentId, childrenByParentId, tasksByAgentId);
  const hasActiveTask = subtreeTasks.some((task) => isActiveTaskStatus(task?.kanbanStatus ?? task?.status));
  const hasQueuedTask = subtreeTasks.some((task) => {
    const taskStatus = getNormalizedTaskStatus(task);
    return taskStatus === "ready" || taskStatus === "ready_to_reason";
  });
  const hasCompletedTask = subtreeTasks.some((task) => getNormalizedTaskStatus(task) === "completed");
  const allTasksCompleted = subtreeTasks.length > 0 && subtreeTasks.every((task) => getNormalizedTaskStatus(task) === "completed");
  const hasRunningDescendant = (() => {
    const stack = [...(childrenByParentId?.get(agentId) ?? [])];
    const seen = new Set();
    while (stack.length) {
      const child = stack.shift();
      const childId = String(child?.id ?? "").trim();
      if (!childId || seen.has(childId)) continue;
      seen.add(childId);
      if (normalizeAgentLifecycleStatus(child?.status) === "running") return true;
      (childrenByParentId?.get(childId) ?? []).forEach((grand) => stack.push(grand));
    }
    return false;
  })();
  const activeTask = subtreeTasks.find((task) => isActiveTaskStatus(task?.kanbanStatus ?? task?.status));

  if (status === "running" || hasActiveTask || hasRunningDescendant) {
    return { status: "running", activeTask };
  }
  if (status === "completed" || status === "answered" || allTasksCompleted) {
    return { status: "completed", activeTask: null };
  }
  if (hasQueuedTask || status === "ready" || status === "ready_to_reason") {
    return { status: "ready", activeTask: null };
  }
  if (status === "awaiting_review" || status === "awaiting_children" || status === "deferred") {
    return { status: "awaiting_children", activeTask: null };
  }
  if (status === "blocked") {
    return { status: "blocked", activeTask: null };
  }
  if (hasCompletedTask) {
    return { status: "completed", activeTask: null };
  }
  return { status, activeTask: null };
}

function summarizeDescendants(agentId, childrenByParentId, tasksByAgentId, claimsByAgentId) {
  const counts = { total: 0, running: 0, queued: 0, awaiting: 0, blocked: 0, completed: 0, claims: 0 };
  let activeLabel = null;
  let latestActiveMs = 0;
  const seen = new Set();
  const visibleChildren = getVisibleSubAgents(agentId, childrenByParentId);
  for (const child of visibleChildren) {
    if (!child || seen.has(child.id)) continue;
    seen.add(child.id);
    counts.total += 1;
    const { status, activeTask } = summarizeDescendantWorkUnit(child, childrenByParentId, tasksByAgentId);
    if (status === "running") counts.running += 1;
    else if (status === "ready" || status === "ready_to_reason") counts.queued += 1;
    else if (status === "awaiting_review" || status === "awaiting_children") counts.awaiting += 1;
    else if (status === "blocked") counts.blocked += 1;
    else if (status === "completed" || status === "answered") counts.completed += 1;
    counts.claims += aggregateSubtreeClaims(child.id, childrenByParentId, claimsByAgentId).length;

    if (status === "running") {
      const candidate = activeTask?.query || activeTask?.metadata?.title || child?.progress?.summary || child?.label || null;
      const ts = getLifecycleTimestampMs(child) ?? 0;
      if (candidate && ts >= latestActiveMs) {
        latestActiveMs = ts;
        activeLabel = String(candidate).trim();
      }
    }
  }
  return { ...counts, activeLabel };
}

function DescendantSummary({ summary, compact = false, hideClaimCount = false }) {
  if (!summary || summary.total === 0) return null;
  const parts = [];
  if (summary.running > 0) parts.push({ label: `${summary.running} running`, color: "text-blue-600" });
  if (summary.awaiting > 0) parts.push({ label: `${summary.awaiting} awaiting`, color: "text-amber-600" });
  if (summary.queued > 0) parts.push({ label: `${summary.queued} queued`, color: "text-slate-500" });
  if (summary.blocked > 0) parts.push({ label: `${summary.blocked} blocked`, color: "text-rose-600" });
  if (summary.completed > 0) parts.push({ label: `${summary.completed} done`, color: "text-emerald-600" });
  if (!hideClaimCount && summary.claims > 0) parts.push({ label: `${summary.claims} claims`, color: "text-slate-500" });

  return (
    <div className={clsx("flex flex-col gap-0.5", compact ? "text-[10px]" : "text-[11px]")}>
      <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
        {parts.map((p, i) => (
          <span key={i} className={clsx("font-medium", p.color)}>
            {p.label}
            {i < parts.length - 1 && <span className="ml-2 text-slate-300">·</span>}
          </span>
        ))}
      </div>
      {summary.activeLabel && (
        <p className="truncate text-slate-500" title={summary.activeLabel}>
          Fetching: {truncateText(summary.activeLabel, 80)}
        </p>
      )}
    </div>
  );
}

// ============================================================
// Compact child row — shown inside an expanded objective
// ============================================================

function ChildAgentRow({ agent, childrenByParentId, tasksByAgentId, claimsByAgentId, depth = 0 }) {
  const [expanded, setExpanded] = useState(false);
  const isWide = useIsWideScreen(768);
  const iconInfo = getRoleIcon(agent);
  const primary = getPrimaryText(agent);
  const secondary = getSecondaryText(agent);
  const agentClaims = useMemo(
    () => aggregateSubtreeClaims(agent.id, childrenByParentId, claimsByAgentId),
    [agent.id, childrenByParentId, claimsByAgentId],
  );
  const agentTasks = useMemo(
    () => aggregateSubtreeTasks(agent.id, childrenByParentId, tasksByAgentId),
    [agent.id, childrenByParentId, tasksByAgentId],
  );
  const visibleSubAgents = useMemo(
    () => getVisibleSubAgents(agent.id, childrenByParentId),
    [agent.id, childrenByParentId],
  );
  const claimCount = agentClaims.length;
  const wp = agent?.work_package ?? {};
  const result = agent?.result ?? {};

  const summary = useMemo(
    () => summarizeDescendants(agent.id, childrenByParentId, tasksByAgentId, claimsByAgentId),
    [agent.id, childrenByParentId, tasksByAgentId, claimsByAgentId],
  );
  const hasActiveTask = useMemo(
    () => agentTasks.some((task) => isActiveTaskStatus(task?.kanbanStatus ?? task?.status)),
    [agentTasks],
  );
  const hasQueuedTask = useMemo(
    () => agentTasks.some((task) => {
      const taskStatus = getNormalizedTaskStatus(task);
      return taskStatus === "queued" || taskStatus === "ready" || taskStatus === "planned";
    }),
    [agentTasks],
  );
  const hasCompletedTask = useMemo(
    () => agentTasks.some((task) => getNormalizedTaskStatus(task) === "completed"),
    [agentTasks],
  );
  const rawStatus = normalizeAgentLifecycleStatus(agent?.status);
  const status =
    !isTerminalAgentLifecycleStatus(rawStatus) && (hasActiveTask || Number(summary?.running ?? 0) > 0)
      ? "running"
      : !isTerminalAgentLifecycleStatus(rawStatus) && (claimCount > 0 || hasCompletedTask) && !hasQueuedTask
        ? "completed"
      : rawStatus;
  const color = getAgentStatusColor(status);

  const summaryShort = String(result?.summary_short ?? "").trim();
  const summaryLong = String(result?.summary_long ?? "").trim();

  // Cap recursion depth defensively
  const canRecurse = depth < 4;

  return (
    <div className={clsx("rounded-lg border", expanded ? "border-blue-200 bg-blue-50/30" : "border-slate-100 bg-slate-50/60")}>
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="flex w-full items-start gap-2 px-3 py-2 text-left"
      >
        <div className="mt-0.5 flex flex-col items-center gap-0.5">
          <StatusDot status={status} />
          <span className={clsx("flex h-5 w-5 items-center justify-center rounded text-[10px] font-bold text-white", iconInfo.color)}>
            {iconInfo.letter}
          </span>
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="truncate text-xs font-medium text-slate-800">{primary}</span>
            <span className={clsx("rounded px-1 py-0.5 text-[9px] font-bold tracking-wide text-white", iconInfo.color)}>
              {iconInfo.label}
            </span>
            <span className={clsx("rounded px-1 py-0.5 text-[9px] font-medium", color.light, color.text)}>
              {formatAgentLifecycleLabel(status)}
            </span>
            {claimCount > 0 && (
              <span className="text-[10px] text-slate-500">{claimCount} claims</span>
            )}
          </div>
          {agent?.id && (
            <p className="mt-0.5 truncate font-mono text-[10px] text-slate-400" title={agent.id}>
              {agent.id}
            </p>
          )}
          {(() => {
            const durationText = formatDurationCompact(agent?.duration_ms);
            if (!durationText) return null;
            const isActive = status === "running";
            const startedAtText = agent?.started_at ? new Date(agent.started_at).toLocaleString() : null;
            const completedAtText = agent?.completed_at ? new Date(agent.completed_at).toLocaleString() : null;
            return (
              <p className="mt-0.5 text-[10px] text-slate-500" title={[startedAtText && `Started: ${startedAtText}`, completedAtText && `Completed: ${completedAtText}`].filter(Boolean).join("\n")}>
                {isActive ? "Running for " : "Took "}
                <span className="font-mono font-medium text-slate-700">{durationText}</span>
              </p>
            );
          })()}
          {summary.total > 0 ? (
            <div className="mt-1">
              <DescendantSummary summary={summary} compact />
            </div>
          ) : null}
        </div>
        <svg
          className={clsx("mt-1 h-3.5 w-3.5 shrink-0 text-slate-400 transition-transform", expanded && "rotate-180")}
          viewBox="0 0 20 20"
          fill="currentColor"
        >
          <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
        </svg>
      </button>

      {expanded && (
        <div className="space-y-3 border-t border-slate-200 px-3 py-3 text-xs">
          {/* Work package — dedupe overlapping mission/objective/question text */}
          {(() => {
            const wpEntries = [
              { label: "Mission", value: wp?.mission, className: "text-xs font-medium text-slate-800", inline: false },
              { label: "Objective", value: wp?.objective, className: "text-xs text-slate-700", inline: true },
              { label: "Question", value: wp?.question, className: "text-xs text-slate-700", inline: true },
            ].filter((e) => String(e.value ?? "").trim());
            const seen = [primary];
            const visible = [];
            for (const e of wpEntries) {
              if (isDuplicateAgentText(e.value, seen)) continue;
              seen.push(e.value);
              visible.push(e);
            }
            const hasFallback = visible.length === 0 && secondary && !isDuplicateAgentText(secondary, [primary]);
            if (visible.length === 0 && !hasFallback) return null;
            return (
              <div className="space-y-1">
                <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-400">Work Package</p>
                {visible.map((e) => (
                  <p key={e.label} className={e.className}>
                    {e.inline ? <><span className="font-medium">{e.label}:</span> {e.value}</> : e.value}
                  </p>
                ))}
                {hasFallback && <p className="text-xs text-slate-500">{secondary}</p>}
              </div>
            );
          })()}

          {/* Result summary — drop short if long subsumes it, drop either if it duplicates header/work package */}
          {(() => {
            const summarySeen = [primary, wp?.mission, wp?.objective, wp?.question].filter(Boolean);
            const showShort = summaryShort && !isDuplicateAgentText(summaryShort, summarySeen);
            if (showShort) summarySeen.push(summaryShort);
            const showLong = summaryLong && !isDuplicateAgentText(summaryLong, summarySeen);
            if (!showShort && !showLong) return null;
            return (
              <div className="space-y-1">
                <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-400">Summary</p>
                {showShort && <p className="text-xs text-slate-700">{summaryShort}</p>}
                {showLong && <p className="text-xs text-slate-500">{summaryLong}</p>}
              </div>
            );
          })()}

          {/* Claims and Tasks — two-column on wide screens, stacked on narrow */}
          {(agentClaims.length > 0 || agentTasks.length > 0) && (
            <div className={clsx(
              agentClaims.length > 0 && agentTasks.length > 0
                ? isWide ? "grid grid-cols-2 gap-4" : "space-y-3"
                : "space-y-3",
            )}>
              {/* Claims column */}
              {agentClaims.length > 0 && (
                <div className="space-y-1">
                  <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-400">Claims ({agentClaims.length})</p>
                  <PaginatedList items={agentClaims} pageSizeOptions={[5, 10, 20]} emptyMessage="">
                    {(pageClaims) => (
                      <div className="space-y-1">
                        {pageClaims.map((claim, i) => (
                          <ClaimRow key={claim.id || i} claim={claim} />
                        ))}
                      </div>
                    )}
                  </PaginatedList>
                </div>
              )}
              {/* Tasks column */}
              {agentTasks.length > 0 && (
                <div className="space-y-1">
                  <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-400">Tasks ({agentTasks.length})</p>
                  <PaginatedList items={agentTasks} pageSizeOptions={[5, 10, 20]} emptyMessage="">
                    {(pageTasks) => (
                      <div className="space-y-1">
                        {pageTasks.map((task, i) => {
                          const tStatus = normalizeAgentLifecycleStatus(task?.kanbanStatus ?? task?.status);
                          const tColor = getAgentStatusColor(tStatus);
                          return (
                            <div key={task.id || i} className="flex items-center gap-2 rounded border border-slate-100 bg-white px-2 py-1">
                              <span className={clsx("h-1.5 w-1.5 shrink-0 rounded-full", tColor.bg)} />
                              <span className="truncate text-[11px] text-slate-700">{truncateText(getTaskDisplayTitle(task), 80)}</span>
                              <span className="ml-auto text-[9px] uppercase text-slate-500">{formatAgentLifecycleLabel(task.status)}</span>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </PaginatedList>
                </div>
              )}
            </div>
          )}

          {/* Nested sub-agents (absorbed children skipped) */}
          {visibleSubAgents.length > 0 && canRecurse && (
            <div className="space-y-1">
              <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-400">Sub-agents ({visibleSubAgents.length})</p>
              <div className="space-y-1.5">
                {visibleSubAgents.map((child) => (
                  <ChildAgentRow
                    key={child.id}
                    agent={child}
                    childrenByParentId={childrenByParentId}
                    tasksByAgentId={tasksByAgentId}
                    claimsByAgentId={claimsByAgentId}
                    depth={depth + 1}
                  />
                ))}
              </div>
            </div>
          )}
          {visibleSubAgents.length > 0 && !canRecurse && (
            <p className="text-[10px] text-slate-400 italic">+ {visibleSubAgents.length} deeper sub-agents (collapsed)</p>
          )}

          {agentClaims.length === 0 && agentTasks.length === 0 && visibleSubAgents.length === 0 && !summaryShort && !summaryLong && (
            <p className="text-[11px] italic text-slate-400">No additional details available.</p>
          )}
        </div>
      )}
    </div>
  );
}

// ============================================================
// Agent detail panel (expanded view)
// ============================================================

function AgentDetailPanel({ agent, tasks, claims, onReview, isReviewing, childrenByParentId, tasksByAgentId, claimsByAgentId }) {
  const [detailTab, setDetailTab] = useState("overview");
  const lifecycleStatus = normalizeAgentLifecycleStatus(agent?.status);
  const isReviewable = lifecycleStatus === "awaiting_review" && agent?.reviewable === true;

  const wp = agent?.work_package ?? {};
  const result = agent?.result ?? {};
  const summaryShort = String(result?.summary_short ?? "").trim();
  const summaryLong = String(result?.summary_long ?? "").trim();
  const synthesizedByLlm = Boolean(result?.synthesized_by_llm);
  // Surface what descendants are actively doing — fetches, claim extracts,
  // searches, etc. — at the objective level, not just the objective's own
  // direct tasks (which is usually empty for delegating objectives).
  const agentTasks = useMemo(
    () => (childrenByParentId
      ? aggregateSubtreeTasks(agent?.id, childrenByParentId, tasksByAgentId ?? new Map())
      : (tasks || []))
      .slice()
      .sort(compareTasksForDisplay),
    [agent?.id, childrenByParentId, tasksByAgentId, tasks],
  );
  const activeTasks = useMemo(
    () => agentTasks.filter((t) => isActiveTaskStatus(t?.kanbanStatus ?? t?.status)),
    [agentTasks],
  );
  const queuedTasks = useMemo(
    () => agentTasks.filter((t) => {
      const rawStatus = getRawTaskStatus(t);
      const status = getNormalizedTaskStatus(t);
      return rawStatus === "queued" || rawStatus === "planned" || status === "ready" || status === "ready_to_reason";
    }),
    [agentTasks],
  );
  const doneTasks = useMemo(
    () => agentTasks.filter((t) => getNormalizedTaskStatus(t) === "completed"),
    [agentTasks],
  );

  // Question agents are absorbed into this objective's principal data;
  // surface their children one level up so the Sub-agents tab still
  // exposes any deeper workers (task owners, evidence agents, sub-objectives)
  // without re-listing the absorbed question itself.
  const visibleSubAgents = useMemo(
    () => getVisibleSubAgents(agent?.id, childrenByParentId),
    [agent?.id, childrenByParentId],
  );
  const hasSubAgents = visibleSubAgents.length > 0;

  return (
    <div className="px-4 pb-4 pt-3">
      {/* AI Insight callout (summary_long above tabs) — dedupe vs header/work package */}
      {synthesizedByLlm && (() => {
        const headerTitle = getPrimaryText(agent) ?? "";
        const insightSeen = [headerTitle, wp?.mission, wp?.objective, wp?.question].filter(Boolean);
        // The card header already shows summary_short; only repeat it
        // here when there is no summary_long worth showing.
        const showLong = summaryLong && !isDuplicateAgentText(summaryLong, insightSeen);
        const showShort = summaryShort && !showLong && !isDuplicateAgentText(summaryShort, insightSeen);
        if (!showShort && !showLong) return null;
        return (
          <div className="mb-3 rounded-md border border-emerald-200 bg-emerald-50/60 px-3 py-2">
            <p className="text-[9px] font-semibold uppercase tracking-wider text-emerald-700">AI Insight</p>
            {showShort && <p className="mt-0.5 text-xs font-medium text-emerald-900">{summaryShort}</p>}
            {showLong && <p className="mt-1 text-[11px] leading-relaxed text-emerald-800/80">{summaryLong}</p>}
          </div>
        );
      })()}

      {/* Detail tabs */}
      <div className="flex gap-4 border-b border-slate-200/70 pb-2 mb-3">
        {[
          { key: "overview", label: "Overview" },
          { key: "claims", label: `Claims (${(claims || []).length})` },
          { key: "tasks", label: `Tasks (${agentTasks.length})` },
          ...(hasSubAgents
            ? [{ key: "children", label: `Sub-agents (${visibleSubAgents.length})` }]
            : []),
          { key: "logs", label: "Logs" },
        ].map((tab) => (
          <button
            key={tab.key}
            onClick={() => setDetailTab(tab.key)}
            className={clsx(
              "text-xs font-medium pb-2 border-b-2 transition",
              detailTab === tab.key
                ? "border-blue-500 text-blue-700"
                : "border-transparent text-slate-500 hover:text-slate-700",
            )}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {detailTab === "overview" && (
        <AgentOverviewContent
          agent={agent}
          tasks={agentTasks}
          activeTasks={activeTasks}
          queuedTasks={queuedTasks}
          doneTasks={doneTasks}
          claims={claims}
          onSwitchTab={setDetailTab}
        />
      )}

      {detailTab === "children" && hasSubAgents && (
        <div className="space-y-2">
          {visibleSubAgents.map((child) => (
            <ChildAgentRow
              key={child.id}
              agent={child}
              childrenByParentId={childrenByParentId}
              tasksByAgentId={tasksByAgentId}
              claimsByAgentId={claimsByAgentId}
            />
          ))}
        </div>
      )}

      {detailTab === "claims" && (
        <PaginatedList items={claims || []} pageSizeOptions={[10, 20, 50]} emptyMessage="No claims produced yet.">
          {(pageClaims) => (
            <div className="space-y-3">
              {pageClaims.map((claim, i) => (
                <div key={claim.id || i} className="rounded-lg border border-slate-100 bg-slate-50 px-3 py-2.5">
                  <div className="flex items-start gap-2">
                    <div className="min-w-0 flex-1">
                      <ClaimRow claim={claim} showStatusDot={false} />
                      <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-[10px] text-slate-500">
                        {claim?.relevance && <span>Relevance: {Math.round(claim.relevance * 100)}%</span>}
                        {claim?.trust && <span>Trust: {claim.trust}</span>}
                        {claim?.source_label && <span>Source: {claim.source_label}</span>}
                        {claim?.status && <span className={clsx(
                          "rounded px-1 py-0.5",
                          claim.status === "verified" ? "bg-emerald-100 text-emerald-700" :
                          claim.status === "rejected" ? "bg-rose-100 text-rose-700" :
                          "bg-amber-100 text-amber-700"
                        )}>{titleizeKey(claim.status)}</span>}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </PaginatedList>
      )}

      {detailTab === "tasks" && (
        <PaginatedList items={agentTasks} pageSizeOptions={[10, 20, 50]} emptyMessage="No tasks assigned.">
          {(pageTasks) => (
            <div className="space-y-2">
              {pageTasks.map((task, i) => (
                <div key={task.id || i} className="flex items-center justify-between rounded-lg border border-slate-100 bg-slate-50 px-3 py-2">
                  <div className="flex items-center gap-2">
                    <span className={clsx("h-2 w-2 rounded-full", getAgentStatusColor(task.status).bg)} />
                    <span className="text-xs text-slate-700">{truncateText(getTaskDisplayTitle(task), 50)}</span>
                  </div>
                  <span className="text-[10px] text-slate-500 uppercase">{formatAgentLifecycleLabel(task.status)}</span>
                </div>
              ))}
            </div>
          )}
        </PaginatedList>
      )}

      {detailTab === "logs" && (
        <div className="space-y-2">
          <p className="text-sm text-slate-500">Agent runtime logs will appear here.</p>
        </div>
      )}

      {/* Review actions */}
      {isReviewable && (
        <div className="mt-4 flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50/70 px-3 py-2">
          <span className="text-xs font-medium text-amber-700">Awaiting review:</span>
          <Button size="sm" color="success" variant="solid" isDisabled={isReviewing} onPress={() => onReview?.({ agentId: agent.id, action: "approve" })}>
            {isReviewing ? "Processing…" : "Approve"}
          </Button>
          <Button size="sm" variant="flat" color="danger" isDisabled={isReviewing} onPress={() => onReview?.({ agentId: agent.id, action: "reject" })}>
            {isReviewing ? "Processing…" : "Request Changes"}
          </Button>
        </div>
      )}
    </div>
  );
}

// ============================================================
// Agent row card (horizontal layout matching screenshot)
// ============================================================

function AgentRow({ agent, tasks, claims, expanded, onToggle, onReview, isReviewing, isStarting = false, isActive: isActiveProp, childrenByParentId, tasksByAgentId, claimsByAgentId, agentsById }) {
  const lifecycleStatus = normalizeAgentLifecycleStatus(agent?.status);
  // Parent can mark a row active when descendants are running (e.g. an
  // awaiting_children objective whose child question is actively working).
  const isActive = isActiveProp ?? (lifecycleStatus === "running");
  const isBlocked = lifecycleStatus === "blocked";
  const isCompleted = lifecycleStatus === "completed" || lifecycleStatus === "answered";
  const isReviewable = lifecycleStatus === "awaiting_review" && agent?.reviewable === true;
  const isAwaiting = lifecycleStatus === "awaiting_review";

  const primaryText = getPrimaryText(agent);
  const secondaryText = getSecondaryText(agent);
  const iconInfo = getRoleIcon(agent);
  const statusColor = getAgentStatusColor(agent?.status);

  // Aggregate subtree: objective absorbs its principal worker
  // (question agent) and their descendants. Counts and detail
  // panel content reflect the full subtree, not just this node.
  const aggregatedTasks = useMemo(
    () =>
      childrenByParentId
        ? aggregateSubtreeTasks(agent?.id, childrenByParentId, tasksByAgentId ?? new Map())
        : tasks ?? [],
    [agent?.id, childrenByParentId, tasksByAgentId, tasks],
  );
  const aggregatedClaims = useMemo(
    () =>
      childrenByParentId
        ? aggregateSubtreeClaims(agent?.id, childrenByParentId, claimsByAgentId ?? new Map())
        : claims ?? [],
    [agent?.id, childrenByParentId, claimsByAgentId, claims],
  );

  const totalTasks = aggregatedTasks.length;
  const doneTaskCount = aggregatedTasks.filter((t) => getNormalizedTaskStatus(t) === "completed").length;
  const claimCount = aggregatedClaims.length;

  // Real elapsed time from agent lifecycle timestamps
  const lastActiveMs = getLifecycleTimestampMs(agent);
  const elapsedText = lastActiveMs ? formatRelativeAge(lastActiveMs) : "";

  // Server-derived duration spanning the agent's full subtree (earliest task
  // start to latest task completion, or now if still in flight).
  const durationText = formatDurationCompact(agent?.duration_ms);
  const startedAtText = agent?.started_at ? new Date(agent.started_at).toLocaleString() : null;
  const completedAtText = agent?.completed_at ? new Date(agent.completed_at).toLocaleString() : null;

  // Active-agents-over-time sparkline (subtree under this objective).
  // Also overlays a tasks series (page fetches, claim extracts, searches…)
  // in a different color, sharing the same time axis.
  const activeAgentsSeries = useMemo(
    () => (agentsById && childrenByParentId
      ? buildActiveAgentsTimeSeries(agent?.id, childrenByParentId, agentsById, tasksByAgentId)
      : null),
    [agent?.id, childrenByParentId, agentsById, tasksByAgentId],
  );

  // Real task progress (done / total)
  const taskProgressColor = isActive ? "blue" : isBlocked ? "rose" : isAwaiting ? "amber" : isCompleted ? "emerald" : "slate";

  const descendantSummary = useMemo(
    () =>
      childrenByParentId
        ? summarizeDescendants(agent?.id, childrenByParentId, tasksByAgentId ?? new Map(), claimsByAgentId ?? new Map())
        : null,
    [agent?.id, childrenByParentId, tasksByAgentId, claimsByAgentId],
  );

  // Synthesized summary from the LLM — surfaced in the card header
  const result = agent?.result ?? {};
  const summaryShort = String(result?.summary_short ?? "").trim();
  const synthesizedByLlm = Boolean(result?.synthesized_by_llm);

  return (
    <div className={clsx(
      "group",
      expanded && "rounded-xl border border-blue-300 bg-blue-50/40 shadow-sm",
      !expanded && isActive && "rounded-xl border border-blue-300 bg-blue-50/30 shadow-sm shadow-blue-100 ring-1 ring-blue-200/50 animate-[active-pulse_2.4s_ease-in-out_infinite]",
    )}>
      <div
        onClick={onToggle}
        className={clsx(
          "flex cursor-pointer items-center gap-3 px-4 py-3 transition",
          expanded
            ? "border-b border-blue-200/60"
            : isActive
              ? "rounded-xl"
              : clsx(
                  "rounded-xl border hover:shadow-md",
                  isReviewable
                    ? "border-amber-300 bg-amber-50/20"
                    : "border-slate-200 bg-white hover:border-slate-300",
                ),
        )}
      >
        {/* Status dot + icon */}
        <div className="flex flex-col items-center gap-1">
          <StatusDot status={agent?.status} forceActive={isActive} />
          <div className={clsx("flex h-9 w-9 items-center justify-center rounded-lg text-xs font-bold text-white", iconInfo.color)}>
            {iconInfo.letter}
          </div>
        </div>

        {/* Name + role + description */}
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold text-slate-900">{primaryText}</span>
            <span className={clsx("rounded px-1.5 py-0.5 text-[10px] font-bold tracking-wide text-white", iconInfo.color)}>
              {iconInfo.label}
            </span>
            {isReviewable && (
              <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-bold text-amber-700">
                REVIEW
              </span>
            )}
            {isStarting && (
              <span className="inline-flex items-center gap-1 rounded bg-blue-100 px-1.5 py-0.5 text-[10px] font-bold text-blue-700">
                <span className="relative inline-flex h-1.5 w-1.5">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-blue-500 opacity-75" />
                  <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-blue-500" />
                </span>
                STARTING…
              </span>
            )}
          </div>
          <p className="mt-0.5 truncate text-xs text-slate-500">{secondaryText || "No description available"}</p>
          {agent?.id && (
            <p className="mt-0.5 truncate font-mono text-[10px] text-slate-400" title={agent.id}>
              {agent.id}
            </p>
          )}
          {summaryShort && (
            <p className="mt-1 line-clamp-2 text-[11px] font-medium text-emerald-800" title={summaryShort}>
              {summaryShort}
            </p>
          )}
          {descendantSummary && descendantSummary.total > 0 && (
            <div className="mt-1">
              <DescendantSummary summary={descendantSummary} hideClaimCount />
            </div>
          )}
          {(elapsedText || durationText) && (
            <p className="mt-0.5 text-[11px] text-slate-400">
              {elapsedText && (
                <>
                  Last active: <span className={clsx(isActive && "text-blue-600 font-medium")}>{elapsedText}</span>
                </>
              )}
              {elapsedText && durationText && <span className="mx-1.5 text-slate-300">·</span>}
              {durationText && (
                <span title={[startedAtText && `Started: ${startedAtText}`, completedAtText && `Completed: ${completedAtText}`].filter(Boolean).join("\n")}>
                  {isActive ? "Running for" : "Took"} <span className="font-medium text-slate-600">{durationText}</span>
                </span>
              )}
            </p>
          )}
        </div>

        {/* Stats: Tasks */}
        <div className="hidden shrink-0 text-center sm:block">
          <div className="text-sm font-semibold text-slate-900">
            {doneTaskCount} <span className="text-slate-400">/ {totalTasks}</span>
          </div>
          <p className="text-[10px] text-slate-500">Tasks</p>
        </div>

        {/* Stats: Claims */}
        <div className="hidden shrink-0 text-center sm:block">
          <div className="text-sm font-semibold text-slate-900">{claimCount}</div>
          <p className="text-[10px] text-slate-500">Claims</p>
        </div>

        {/* Stats: Duration */}
        {durationText && (
          <div className="hidden shrink-0 text-center sm:block">
            <div className="text-sm font-semibold font-mono text-slate-900">{durationText}</div>
            <p className="text-[10px] text-slate-500">{isActive ? "Running" : "Duration"}</p>
          </div>
        )}

        {/* Sparkline: active agents (blue) and backend tasks (green) over
            time across this objective's subtree. */}
        {activeAgentsSeries?.series?.length ? (
          <div className="hidden shrink-0 flex-col items-center sm:flex">
            <ActiveAgentsSparkline
              data={activeAgentsSeries}
              width={96}
              height={22}
              stroke={isActive ? "rgb(37 99 235)" : "rgb(100 116 139)"}
              fill={isActive ? "rgba(59,130,246,0.18)" : "rgba(100,116,139,0.14)"}
            />
            <p className="text-[10px] text-slate-500">
              <span className="text-blue-600 font-medium">A {activeAgentsSeries.peak}</span>
              {activeAgentsSeries.taskPeak > 0 && (
                <>
                  {" · "}
                  <span className="text-emerald-600 font-medium">T {activeAgentsSeries.taskPeak}</span>
                </>
              )}
            </p>
          </div>
        ) : null}

        {/* Task progress + elapsed */}
        <div className="hidden shrink-0 flex-col items-end gap-1 sm:flex">
          {elapsedText && <div className="text-xs font-mono text-slate-600">{elapsedText}</div>}
          <TaskProgressBar done={doneTaskCount} total={totalTasks} color={taskProgressColor} />
        </div>

        {/* Expand chevron */}
        <div className="shrink-0">
          <svg
            className={clsx("h-4 w-4 text-slate-400 transition-transform", expanded && "rotate-180")}
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
          </svg>
        </div>
      </div>

      {/* Expanded detail panel */}
      {expanded && (
        <AgentDetailPanel
          agent={agent}
          tasks={aggregatedTasks}
          claims={aggregatedClaims}
          onReview={onReview}
          isReviewing={isReviewing}
          childrenByParentId={childrenByParentId}
          tasksByAgentId={tasksByAgentId}
          claimsByAgentId={claimsByAgentId}
        />
      )}
    </div>
  );
}

// ============================================================
// Grouped section header (Queued, Completed, Blocked...)
// ============================================================

function AgentGroup({ title, subtitle, agents, tasksByAgentId, claimsByAgentId, childrenByParentId, agentsById, expandedId, onToggle, onReview, reviewSubmittingId, recentlyApprovedIds, icon, iconColor }) {
  const [collapsed, setCollapsed] = useState(false);

  if (!agents.length) return null;

  return (
    <div className="space-y-2">
      <button
        onClick={() => setCollapsed(!collapsed)}
        className="flex w-full items-center justify-between rounded-lg px-2 py-2 text-left transition hover:bg-slate-50"
      >
        <div className="flex items-center gap-2">
          <span className={clsx("flex h-6 w-6 items-center justify-center rounded-full text-xs", iconColor)}>
            {icon}
          </span>
          <span className="text-sm font-semibold text-slate-900">{title} ({agents.length})</span>
          <span className="text-xs text-slate-500">{subtitle}</span>
        </div>
        <div className="flex items-center gap-1">
          {/* Mini avatars */}
          {agents.slice(0, 5).map((a, i) => (
            <span key={a.id || i} className={clsx("h-5 w-5 rounded-full text-[9px] font-bold text-white flex items-center justify-center", getRoleIcon(a).color)}>
              {getRoleIcon(a).letter}
            </span>
          ))}
          {agents.length > 5 && (
            <span className="text-xs text-slate-500">+{agents.length - 5}</span>
          )}
          <svg className={clsx("ml-1 h-4 w-4 text-slate-400 transition", collapsed && "-rotate-90")} viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
          </svg>
        </div>
      </button>

      {!collapsed && (
        <div className="space-y-2 pl-2">
          {agents.map((agent) => (
            <AgentRow
              key={agent.id}
              agent={agent}
              tasks={tasksByAgentId?.get(agent.id)}
              claims={claimsByAgentId?.get(agent.id)}
              childrenByParentId={childrenByParentId}
              tasksByAgentId={tasksByAgentId}
              claimsByAgentId={claimsByAgentId}
              agentsById={agentsById}
              expanded={expandedId === agent.id}
              onToggle={() => onToggle(expandedId === agent.id ? null : agent.id)}
              onReview={onReview}
              isReviewing={reviewSubmittingId === agent.id}
              isStarting={Boolean(recentlyApprovedIds?.has(agent.id)) || agent.status === "ready" || agent.status === "ready_to_reason"}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ============================================================
// Main AgentsPane component
// ============================================================

function AgentsPane({ data, onReviewAction, reviewSubmittingAgentId }) {
  const [viewTab, setViewTab] = useState("agents");
  const [expandedAgentId, setExpandedAgentId] = useState(null);
  // Agents the user just approved — surfaced in a "Spinning Up" group with
  // a pulsing pill until the server-side status moves into active or terminal
  // work. The backend can legitimately pass through awaiting_children while it
  // creates child work, so keep the optimistic marker through that handoff.
  const [recentlyApprovedIds, setRecentlyApprovedIds] = useState(() => new Set());
  const nowMs = useClock(1000);

  const plannedAgentUnits = toArray(data?.plannedAgentUnits ?? []);
  const claims = toArray(data?.claims);
  // questions/tasks may arrive as either a raw array or a {scope, tasks/questions}
  // snapshot object from buildTasksSnapshot/buildQuestionsSnapshot. Unwrap the
  // inner array in the snapshot case so downstream lookups work.
  const rawQuestions = data?.questions;
  const questions = toArray(
    rawQuestions && !Array.isArray(rawQuestions) && Array.isArray(rawQuestions.questions)
      ? rawQuestions.questions
      : rawQuestions,
  );
  const rawTasks = data?.tasks;
  const tasks = toArray(
    rawTasks && !Array.isArray(rawTasks) && Array.isArray(rawTasks.tasks)
      ? rawTasks.tasks
      : rawTasks,
  );

  // Normalize and sort agents by recency (active first)
  const normalizedAgents = useMemo(() => {
    return plannedAgentUnits.map((agent) => ({
      ...agent,
      status: normalizeAgentLifecycleStatus(agent?.status),
    }));
  }, [plannedAgentUnits]);

  const runtimeActiveAgentIds = useMemo(() => {
    const ids = new Set();
    const addAgentId = (value) => {
      const id = String(value ?? "").trim();
      if (id) ids.add(id);
    };
    const addAgent = (agent) => addAgentId(agent?.id ?? agent?.agent_id ?? agent);
    const agentRuntime = data?.agentRuntime ?? data?.agent_runtime ?? {};
    const runtimeV3 = data?.researchRuntimeV3 ?? data?.runtimeV3 ?? data?.research_runtime_v3 ?? {};
    toArray(agentRuntime?.active_agents).forEach(addAgent);
    toArray(runtimeV3?.active_agents).forEach(addAgent);
    addAgentId(agentRuntime?.current_agent_id);
    addAgentId(runtimeV3?.current_agent_id);
    return ids;
  }, [data?.agentRuntime, data?.agent_runtime, data?.researchRuntimeV3, data?.runtimeV3, data?.research_runtime_v3]);

  // Build parent -> children map for the agent tree (root -> objectives -> questions -> ...)
  const childrenByParentId = useMemo(() => {
    const map = new Map();
    normalizedAgents.forEach((agent) => {
      const parentId = String(agent?.parent_agent_id ?? "").trim();
      if (!parentId) return;
      if (!map.has(parentId)) map.set(parentId, []);
      map.get(parentId).push(agent);
    });
    return map;
  }, [normalizedAgents]);

  // Flat lookup map for fast subtree traversals (sparkline timing series).
  const agentsById = useMemo(() => {
    const map = new Map();
    normalizedAgents.forEach((agent) => {
      const id = String(agent?.id ?? "").trim();
      if (id) map.set(id, agent);
    });
    return map;
  }, [normalizedAgents]);

  // Identify the root agent so we can pin top-level rows to its direct
  // children only. Nested sub-objectives (parent is another objective) get
  // rolled up under their parent rather than shown as their own rows.
  const rootAgentIds = useMemo(() => {
    const ids = new Set();
    normalizedAgents.forEach((agent) => {
      if (agent?.node_role === "root") {
        const id = String(agent?.id ?? "").trim();
        if (id) ids.add(id);
      }
    });
    return ids;
  }, [normalizedAgents]);

  // Top-level rows = layer-2 objective agents (root = layer 1). An objective
  // counts as layer 2 when it has no parent or its parent is the root. Nested
  // objectives, including matrix cells, are surfaced via their ancestor row.
  // If a parent is missing from the visible list, keep the child out of the
  // peer groups rather than promoting it as a misleading root-level objective.
  const objectiveAgents = useMemo(() => {
    return normalizedAgents.filter((agent) => {
      if (agent?.node_role !== "objective") return false;
      const parentId = String(agent?.parent_agent_id ?? "").trim();
      if (!parentId) return true;
      if (rootAgentIds.has(parentId)) return true;
      return false;
    });
  }, [normalizedAgents, rootAgentIds]);

  // Build lookups. Task -> agent linkage covers direct ownership only.
  // Subtree rollup walks childrenByParentId so descendant agents still
  // contribute their own work without attributing unrelated objective/question
  // matches to a parent.
  const tasksByAgentId = useMemo(() => {
    const mapping = new Map();
    normalizedAgents.forEach((agent) => {
      const agentId = agent.id;
      if (!agentId) return;
      mapping.set(
        agentId,
        tasks.filter((task) =>
          task?.unit_id === agentId ||
          task?.agent_thread_id === agentId ||
          task?.agent_id === agentId,
        ),
      );
    });
    return mapping;
  }, [normalizedAgents, tasks]);

  // An objective is "active" when it itself is running, any descendant
  // (question agent, sub-objective, task owner) is running, OR directly-owned
  // or descendant backend tasks are runnable/running. Otherwise a parent with
  // active fetch/search work can look idle.
  const hasRunningDescendant = useMemo(() => {
    const cache = new Map();
    const compute = (id) => {
      if (cache.has(id)) return cache.get(id);
      cache.set(id, false); // guard cycles
      const children = childrenByParentId.get(id) ?? [];
      let active = false;
      for (const child of children) {
        const status = normalizeAgentLifecycleStatus(child?.status);
        if (
          status === "running" ||
          (!isTerminalAgentLifecycleStatus(status) && runtimeActiveAgentIds.has(String(child?.id ?? "").trim()))
        ) { active = true; break; }
        if (compute(child.id)) { active = true; break; }
      }
      cache.set(id, active);
      return active;
    };
    return (agentId) => compute(agentId);
  }, [childrenByParentId, runtimeActiveAgentIds]);

  const hasActiveSubtreeTask = useMemo(() => {
    const cache = new Map();
    const compute = (id) => {
      if (cache.has(id)) return cache.get(id);
      cache.set(id, false);
      const directTasks = tasksByAgentId.get(id) ?? [];
      if (directTasks.some((task) => isActiveTaskStatus(task?.kanbanStatus ?? task?.status))) {
        cache.set(id, true);
        return true;
      }
      const children = childrenByParentId.get(id) ?? [];
      for (const child of children) {
        if (compute(child.id)) {
          cache.set(id, true);
          return true;
        }
      }
      cache.set(id, false);
      return false;
    };
    return (agentId) => compute(agentId);
  }, [childrenByParentId, tasksByAgentId]);

  const activeAgents = useMemo(
    () => objectiveAgents.filter((a) =>
      a.status === "running" ||
      (!isTerminalAgentLifecycleStatus(a.status) && runtimeActiveAgentIds.has(String(a?.id ?? "").trim())) ||
      hasRunningDescendant(a.id) ||
      hasActiveSubtreeTask(a.id),
    ),
    [objectiveAgents, hasRunningDescendant, hasActiveSubtreeTask, runtimeActiveAgentIds],
  );
  const activeAgentIds = useMemo(() => new Set(activeAgents.map((a) => a.id)), [activeAgents]);
  // "Spinning up" = real ready/ready_to_reason agents + optimistically
  // approved agents that haven't yet transitioned on the server.
  // Excludes anything already promoted into Active by descendant activity.
  const spinningUpAgents = useMemo(() => {
    const real = objectiveAgents.filter(
      (a) => (a.status === "ready" || a.status === "ready_to_reason") && !activeAgentIds.has(a.id),
    );
    if (recentlyApprovedIds.size === 0) return real;
    const optimistic = objectiveAgents.filter((a) =>
      recentlyApprovedIds.has(a.id) &&
      (a.status === "awaiting_review" || a.status === "awaiting_children") &&
      !activeAgentIds.has(a.id),
    );
    const seen = new Set(real.map((a) => a.id));
    optimistic.forEach((a) => { if (!seen.has(a.id)) { real.push(a); seen.add(a.id); } });
    return real;
  }, [objectiveAgents, recentlyApprovedIds, activeAgentIds]);
  // Waiting = awaiting_children / deferred AND no descendant is currently
  // running (otherwise the parent is genuinely active).
  const waitingAgents = useMemo(
    () => objectiveAgents.filter(
      (a) =>
        (a.status === "awaiting_children" || a.status === "deferred") &&
        !activeAgentIds.has(a.id) &&
        !recentlyApprovedIds.has(a.id),
    ),
    [objectiveAgents, activeAgentIds, recentlyApprovedIds],
  );
  // Awaiting Review excludes agents the user just approved (they live in
  // Spinning Up instead).
  const awaitingReviewAgents = useMemo(() =>
    objectiveAgents.filter((a) => a.status === "awaiting_review" && !recentlyApprovedIds.has(a.id)),
  [objectiveAgents, recentlyApprovedIds]);
  const completedAgents = useMemo(
    () => objectiveAgents.filter((a) => (a.status === "completed" || a.status === "answered") && !activeAgentIds.has(a.id)),
    [objectiveAgents, activeAgentIds],
  );
  const blockedAgents = useMemo(
    () => objectiveAgents.filter((a) => a.status === "blocked" && !activeAgentIds.has(a.id)),
    [objectiveAgents, activeAgentIds],
  );
  const readyAgents = spinningUpAgents; // alias kept for header/legend compat

  // Header counts mirror the objective agents that are actually rendered
  // below (layer-2 objectives), so the stacked progress bar adds up to the
  // total agent count and matches the per-status group sections.
  const fleetActive = activeAgents;
  const fleetReady = readyAgents;
  const fleetCompleted = completedAgents;
  const fleetBlocked = blockedAgents;

  // Run-level active agents-over-time sparkline series. Walks the entire
  // agent set (every objective, question, etc.) and counts concurrent agents
  // at evenly-spaced sample points. Two overlaid series: agents (blue) and
  // backend tasks (page fetches, claim extracts, searches…) in green.
  const runActiveAgentsSeries = useMemo(() => {
    const nowMs = Date.now();
    const agentIntervals = normalizedAgents
      .map((a) => intervalFromEntity(a, nowMs))
      .filter(Boolean);
    const taskIntervals = tasks
      .map((t) => intervalFromEntity(t, nowMs))
      .filter(Boolean);
    if (typeof window !== "undefined" && window.__DEBUG_SPARKLINE) {
      // eslint-disable-next-line no-console
      console.log("[sparkline] run-level", {
        agents: normalizedAgents.length,
        agentIntervals: agentIntervals.length,
        tasks: tasks.length,
        taskIntervals: taskIntervals.length,
        sampleTask: tasks[0] && {
          id: tasks[0].id,
          status: tasks[0].status,
          queued_at_ms: tasks[0].queued_at_ms,
          started_at_ms: tasks[0].started_at_ms,
          completed_at_ms: tasks[0].completed_at_ms,
        },
      });
    }
    if (!agentIntervals.length && !taskIntervals.length) return null;
    const allIntervals = [...agentIntervals, ...taskIntervals];
    const tMin = Math.min(...allIntervals.map((iv) => iv.start));
    const tMax = Math.max(...allIntervals.map((iv) => iv.end));
    const steps = 48;
    const agents = bucketIntervalsToSeries(agentIntervals, tMin, tMax, steps);
    const tasksSeries = bucketIntervalsToSeries(taskIntervals, tMin, tMax, steps);
    return {
      series: agents, // back-compat
      agents,
      tasks: tasksSeries,
      tMin,
      tMax: tMax > tMin ? tMax : tMin + 1,
      peak: agents.length ? Math.max(...agents) : 0,
      taskPeak: tasksSeries.length ? Math.max(...tasksSeries) : 0,
      agentCount: agentIntervals.length,
      taskCount: taskIntervals.length,
    };
  }, [normalizedAgents, tasks]);

  const claimsByAgentId = useMemo(() => {
    const mapping = new Map();
    normalizedAgents.forEach((agent) => {
      const agentId = String(agent?.id ?? "").trim();
      if (!agentId) return;
      const agentClaims = [];
      const producedIds = new Set(toArray(agent?.produced_claim_ids));
      claims.forEach((claim) => {
        if (producedIds.has(claim.id)) {
          agentClaims.push(claim);
          return;
        }
        if (
          toArray(claim?.originating_agent_ids).includes(agentId) ||
          toArray(claim?.originating_unit_ids).includes(agentId) ||
          toArray(claim?.originating_agent_thread_ids).includes(agentId)
        ) {
          agentClaims.push(claim);
        }
      });
      mapping.set(agentId, agentClaims);
    });
    return mapping;
  }, [normalizedAgents, claims]);

  const handleReview = useCallback(
    async ({ agentId, action }) => {
      if (!agentId) return;
      if (action === "approve") {
        setRecentlyApprovedIds((prev) => {
          const next = new Set(prev);
          next.add(agentId);
          return next;
        });
      }
      try {
        await onReviewAction?.({ agentId, action });
      } catch {
        // Error handled by parent
      }
    },
    [onReviewAction],
  );

  // Once an approved agent has visibly moved into active/terminal work on the
  // server, drop it from the optimistic set so the pulsing pill resolves into
  // whatever group its real status places it.
  useEffect(() => {
    if (recentlyApprovedIds.size === 0) return;
    const optimisticStatuses = new Map(
      normalizedAgents
        .filter((a) => a?.status === "awaiting_review" || a?.status === "awaiting_children" || a?.status === "ready" || a?.status === "ready_to_reason")
        .map((a) => [a.id, a.status]),
    );
    let changed = false;
    const next = new Set(recentlyApprovedIds);
    recentlyApprovedIds.forEach((id) => {
      if (!optimisticStatuses.has(id)) {
        next.delete(id);
        changed = true;
      }
    });
    if (changed) setRecentlyApprovedIds(next);
  }, [normalizedAgents, recentlyApprovedIds]);

  const fleetAwaiting = awaitingReviewAgents;
  const total = objectiveAgents.length;
  const pctActive = total > 0 ? Math.round((fleetActive.length / total) * 100) : 0;
  const pctReady = total > 0 ? Math.round((fleetReady.length / total) * 100) : 0;
  const pctAwaiting = total > 0 ? Math.round((fleetAwaiting.length / total) * 100) : 0;
  const pctCompleted = total > 0 ? Math.round((fleetCompleted.length / total) * 100) : 0;
  const pctBlocked = total > 0 ? Math.round((fleetBlocked.length / total) * 100) : 0;

  const liveActive = fleetActive.length > 0;

  // Freeze the overall-progress clock when the run is paused (no agents
  // running). We use the latest activity timestamp across all agents as
  // the freeze point so the clock resumes correctly when the user later
  // approves and work picks up again. Hooks must be called unconditionally
  // before any early return.
  const runEndMs = useMemo(() => {
    if (fleetActive.length > 0) return null; // tick with nowMs
    let latest = 0;
    normalizedAgents.forEach((a) => {
      const candidates = [
        Number(a?.completed_at_ms),
        a?.completed_at ? new Date(a.completed_at).getTime() : NaN,
        Number(a?.updated_at_ms),
        a?.updated_at ? new Date(a.updated_at).getTime() : NaN,
      ].filter((v) => Number.isFinite(v) && v > 0);
      if (candidates.length) {
        latest = Math.max(latest, ...candidates);
      }
    });
    return latest > 0 ? latest : null;
  }, [fleetActive.length, normalizedAgents]);
  const overallProgressEndMs = liveActive ? nowMs : (runEndMs ?? nowMs);

  if (!plannedAgentUnits.length && !questions.length && !tasks.length) {
    return <p className="text-sm text-slate-500">No agent activity data is available yet.</p>;
  }

  return (
    <div className="h-full overflow-auto space-y-4">
      {/* Header with progress bar and total count */}
      <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
        <div className="flex items-center justify-between mb-3 gap-3">
          <div className="flex items-center gap-3">
            <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Overall Progress</p>
            <LiveIndicator active={liveActive} />
            <span
              className="font-mono text-[11px] text-slate-500"
              title={[
                runActiveAgentsSeries?.tMin && `Started: ${new Date(runActiveAgentsSeries.tMin).toLocaleString()}`,
                !liveActive && runEndMs && `Paused at: ${new Date(runEndMs).toLocaleString()}`,
              ].filter(Boolean).join("\n") || undefined}
            >
              {runActiveAgentsSeries?.tMin
                ? formatRuntimeFromStart(runActiveAgentsSeries.tMin, overallProgressEndMs)
                : "--:--:--"}
              {!liveActive && runEndMs ? <span className="ml-1 text-slate-400">(paused)</span> : null}
            </span>
          </div>
          <div className="flex items-center gap-3">
            {runActiveAgentsSeries?.series?.length ? (
              <div className="flex flex-col items-center rounded-lg border border-slate-200 bg-slate-50 px-3 py-1.5">
                <ActiveAgentsSparkline
                  data={runActiveAgentsSeries}
                  width={144}
                  height={28}
                  stroke="rgb(37 99 235)"
                  fill="rgba(59,130,246,0.18)"
                />
                <p className="mt-0.5 flex items-center gap-2 text-[10px] text-slate-500">
                  <span className="inline-flex items-center gap-1">
                    <span className="h-1.5 w-3 rounded-sm bg-blue-500" />
                    Agents · <span className="font-semibold text-slate-700">{runActiveAgentsSeries.peak}</span>
                  </span>
                  {runActiveAgentsSeries.taskPeak > 0 && (
                    <span className="inline-flex items-center gap-1">
                      <span className="h-1.5 w-3 rounded-sm bg-emerald-500" />
                      Tasks · <span className="font-semibold text-slate-700">{runActiveAgentsSeries.taskPeak}</span>
                    </span>
                  )}
                </p>
              </div>
            ) : null}
            <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-center">
              <p className="text-xl font-bold text-slate-900">{total}</p>
              <p className="text-[10px] text-slate-500">Total Agents</p>
            </div>
          </div>
        </div>

        {/* Progress bar with percentages */}
        <div className="relative h-6 w-full overflow-hidden rounded-lg bg-slate-100">
          <div className="absolute inset-0 flex text-[10px] font-bold text-white">
            {fleetActive.length > 0 && (
              <div className="flex h-full items-center justify-center bg-blue-500 transition-all" style={{ width: `${pctActive}%` }}>
                {pctActive >= 12 && `${pctActive}%`}
              </div>
            )}
            {fleetAwaiting.length > 0 && (
              <div className="flex h-full items-center justify-center bg-amber-400 transition-all" style={{ width: `${pctAwaiting}%` }}>
                {pctAwaiting >= 12 && `${pctAwaiting}%`}
              </div>
            )}
            {fleetReady.length > 0 && (
              <div className="flex h-full items-center justify-center bg-amber-500 transition-all" style={{ width: `${pctReady}%` }}>
                {pctReady >= 12 && `${pctReady}%`}
              </div>
            )}
            {fleetCompleted.length > 0 && (
              <div className="flex h-full items-center justify-center bg-emerald-500 transition-all" style={{ width: `${pctCompleted}%` }}>
                {pctCompleted >= 12 && `${pctCompleted}%`}
              </div>
            )}
            {fleetBlocked.length > 0 && (
              <div className="flex h-full items-center justify-center bg-rose-500 transition-all" style={{ width: `${pctBlocked}%` }}>
                {pctBlocked >= 12 && `${pctBlocked}%`}
              </div>
            )}
          </div>
        </div>

        {/* Legend */}
        <div className="mt-2 flex flex-wrap gap-3 text-xs">
          {fleetActive.length > 0 && (
            <span className="inline-flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-blue-500" /> Active ({fleetActive.length})</span>
          )}
          {fleetAwaiting.length > 0 && (
            <span className="inline-flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-amber-400" /> Awaiting Review ({fleetAwaiting.length})</span>
          )}
          {fleetReady.length > 0 && (
            <span className="inline-flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-amber-500" /> Queued ({fleetReady.length})</span>
          )}
          {fleetCompleted.length > 0 && (
            <span className="inline-flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-emerald-500" /> Completed ({fleetCompleted.length})</span>
          )}
          {fleetBlocked.length > 0 && (
            <span className="inline-flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-rose-500" /> Blocked ({fleetBlocked.length})</span>
          )}
        </div>
      </div>

      {/* Sub-tabs */}
      <div className="flex gap-2">
        {[
          { key: "agents", label: "Agents", count: objectiveAgents.length },
          { key: "tasks", label: "Tasks", count: tasks.length },
        ].map((tab) => (
          <button
            key={tab.key}
            type="button"
            onClick={() => { setViewTab(tab.key); setExpandedAgentId(null); }}
            className={clsx(
              "rounded-xl px-3 py-2 text-sm font-medium transition",
              viewTab === tab.key
                ? "bg-slate-900 text-white"
                : "bg-white text-slate-600 hover:bg-slate-100 border border-slate-200",
            )}
          >
            {tab.label} {tab.count > 0 && <span className="ml-1 opacity-70">({tab.count})</span>}
          </button>
        ))}
      </div>

      {/* Agents view — grouped by status */}
      {viewTab === "agents" && (
        <div className="space-y-4">
          {/* Active objectives first */}
          {activeAgents.length > 0 && (
            <div className="space-y-2">
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">Active Objectives</p>
              {activeAgents.map((agent) => (
                <AgentRow
                  key={agent.id}
                  agent={agent}
                  tasks={tasksByAgentId.get(agent.id)}
                  claims={claimsByAgentId.get(agent.id)}
                  childrenByParentId={childrenByParentId}
                  tasksByAgentId={tasksByAgentId}
                  claimsByAgentId={claimsByAgentId}
                  agentsById={agentsById}
                  expanded={expandedAgentId === agent.id}
                  onToggle={() => setExpandedAgentId(expandedAgentId === agent.id ? null : agent.id)}
                  onReview={handleReview}
                  isReviewing={reviewSubmittingAgentId === agent.id}
                  isStarting={false}
                  isActive={true}
                />
              ))}
            </div>
          )}

          {/* Awaiting review */}
          {awaitingReviewAgents.length > 0 && (
            <AgentGroup
              title="Awaiting Review"
              subtitle="Objectives requiring approval"
              agents={awaitingReviewAgents}
              tasksByAgentId={tasksByAgentId}
              claimsByAgentId={claimsByAgentId}
              childrenByParentId={childrenByParentId}
              agentsById={agentsById}
              expandedId={expandedAgentId}
              onToggle={setExpandedAgentId}
              onReview={handleReview}
              reviewSubmittingId={reviewSubmittingAgentId}
              icon="R"
              iconColor="bg-amber-100 text-amber-700"
            />
          )}

          {/* Spinning Up — approved or ready, awaiting first run */}
          {spinningUpAgents.length > 0 && (
            <AgentGroup
              title="Spinning Up"
              subtitle="Approved objectives starting their first run"
              agents={spinningUpAgents}
              tasksByAgentId={tasksByAgentId}
              claimsByAgentId={claimsByAgentId}
              childrenByParentId={childrenByParentId}
              agentsById={agentsById}
              expandedId={expandedAgentId}
              onToggle={setExpandedAgentId}
              onReview={handleReview}
              reviewSubmittingId={reviewSubmittingAgentId}
              recentlyApprovedIds={recentlyApprovedIds}
              icon="↑"
              iconColor="bg-blue-100 text-blue-700"
            />
          )}

          {/* Waiting — awaiting children or deferred */}
          {waitingAgents.length > 0 && (
            <AgentGroup
              title="Waiting"
              subtitle="Objectives paused for child work or budget"
              agents={waitingAgents}
              tasksByAgentId={tasksByAgentId}
              claimsByAgentId={claimsByAgentId}
              childrenByParentId={childrenByParentId}
              agentsById={agentsById}
              expandedId={expandedAgentId}
              onToggle={setExpandedAgentId}
              onReview={handleReview}
              reviewSubmittingId={reviewSubmittingAgentId}
              icon="…"
              iconColor="bg-slate-100 text-slate-600"
            />
          )}

          {/* Completed */}
          {completedAgents.length > 0 && (
            <AgentGroup
              title="Completed Objectives"
              subtitle="Successfully completed objectives"
              agents={completedAgents}
              tasksByAgentId={tasksByAgentId}
              claimsByAgentId={claimsByAgentId}
              childrenByParentId={childrenByParentId}
              agentsById={agentsById}
              expandedId={expandedAgentId}
              onToggle={setExpandedAgentId}
              onReview={handleReview}
              reviewSubmittingId={reviewSubmittingAgentId}
              icon="✓"
              iconColor="bg-emerald-100 text-emerald-700"
            />
          )}

          {/* Blocked */}
          {blockedAgents.length > 0 && (
            <AgentGroup
              title="Blocked Objectives"
              subtitle="Objectives blocked by issues or dependencies"
              agents={blockedAgents}
              tasksByAgentId={tasksByAgentId}
              claimsByAgentId={claimsByAgentId}
              childrenByParentId={childrenByParentId}
              agentsById={agentsById}
              expandedId={expandedAgentId}
              onToggle={setExpandedAgentId}
              onReview={handleReview}
              reviewSubmittingId={reviewSubmittingAgentId}
              icon="!"
              iconColor="bg-rose-100 text-rose-700"
            />
          )}

          {objectiveAgents.length === 0 && (
            <p className="text-sm text-slate-500">No objective agents have been planned yet.</p>
          )}
        </div>
      )}

      {/* Tasks view */}
      {viewTab === "tasks" && <TasksView tasks={tasks} tasksByAgentId={tasksByAgentId} />}

      {/* Auto-update footer */}
      <div className="flex items-center justify-center gap-2 pt-1 pb-2 text-[10px] text-slate-400">
        <span className={clsx("h-1.5 w-1.5 rounded-full", liveActive ? "bg-emerald-500 animate-pulse" : "bg-slate-300")} />
        <span>{liveActive ? "Auto-updating every 3 seconds" : "Idle — no active agents"}</span>
      </div>
    </div>
  );
}

// ============================================================
// Tasks sub-view (compact rows grouped by agent)
// ============================================================

function TasksView({ tasks, tasksByAgentId }) {
  if (!tasks.length) {
    return <p className="text-sm text-slate-500">No tasks are queued right now.</p>;
  }

  const sorted = [...tasks].sort((a, b) => {
    const statusOrder = { active: 0, open: 1, blocked: 2, deferred: 3, closed: 4, skipped: 5 };
    const aStatus = normalizeAgentLifecycleStatus(a?.kanbanStatus ?? a?.status);
    const bStatus = normalizeAgentLifecycleStatus(b?.kanbanStatus ?? b?.status);
    return (statusOrder[aStatus] ?? 9) - (statusOrder[bStatus] ?? 9);
  });

  // Group by agent
  const tasksByAgent = {};
  const unassigned = [];
  sorted.forEach((task) => {
    const agentId = task?.unit_id || task?.agent_thread_id || task?.agent_id;
    if (agentId && tasksByAgentId.get(agentId)?.length > 0) {
      if (!tasksByAgent[agentId]) tasksByAgent[agentId] = [];
      tasksByAgent[agentId].push(task);
    } else {
      unassigned.push(task);
    }
  });

  return (
    <div className="space-y-4">
      {Object.entries(tasksByAgent).map(([agentId, agentTasks]) => (
        <div key={agentId} className="space-y-2">
          <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">
            {agentId.split(":").pop()?.replace(/_/g, " ") || agentId}
          </p>
          {agentTasks.map((task) => (
            <TaskRow key={task.id} task={task} />
          ))}
        </div>
      ))}
      {unassigned.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">Unassigned</p>
          {unassigned.map((task) => (
            <TaskRow key={task.id} task={task} />
          ))}
        </div>
      )}
    </div>
  );
}

function TaskRow({ task }) {
  const normalizedStatus = getNormalizedTaskStatus(task);
  const color = getAgentStatusColor(normalizedStatus);

  return (
    <div className="flex items-center gap-3 rounded-lg border border-slate-200 bg-white px-3 py-2">
      <span className={clsx("h-2 w-2 rounded-full", color.bg)} />
      <div className="min-w-0 flex-1">
        <p className="text-xs font-medium text-slate-900">{truncateText(getTaskDisplayTitle(task), 60)}</p>
        <div className="mt-0.5 flex gap-2 text-[10px] text-slate-500">
          <span className={clsx("rounded px-1.5 py-0.5 font-medium", color.light, color.text)}>{formatAgentLifecycleLabel(task.status)}</span>
          {task.type && <span>{titleizeKey(task.type)}</span>}
          {task.phase && <span>{titleizeKey(task.phase)}</span>}
        </div>
      </div>
    </div>
  );
}

export { AgentsPane };
